import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { installPrepApi, tsfadApi } from '../services/api';
import { getAuthToken } from '../services/auth';
import { getBaseUrl } from '../services/baseUrl';
import { showInAppAlert, showInAppConfirm } from '../services/inAppDialog';
import { promptCaseCloseAfterServiceReport } from '../utils/serviceReportCaseClose';
import { CaseAttachmentViewerModal } from './CaseAttachmentViewerModal';
import RepairServiceReportModal from './RepairServiceReportModal';
import type { ReferenceImageBlock, ReferenceImageHit } from './TSFADAnalysis.shared';
import type { ReportTemplate, ServiceReportDocument, ServiceReportRecord } from '../utils/serviceReportTemplate';

type StepCapture = {
  type?: string;
  parameter?: string;
  units?: string;
  target?: number | string | null;
  expectedLow?: number | string | null;
  expectedHigh?: number | string | null;
  tolerance?: number | string | null;
  method?: string;
  tool?: string;
  notes?: string;
};

type ChecklistItem = {
  id: string;
  label: string;
  spec?: string | null;
  completed: boolean;
  notes?: string;
  reference?: string | null;
  sourceNodeIds?: string[];
  capture?: StepCapture | null;
  captureValue?: string;
};

type DeviceExpertContextOption = {
  key: string;
  stepId: string;
  number: string;
  description: string;
  label: string;
};

type DeviceExpertParentOption = {
  id: string;
  sequence: number;
  label: string;
  checklistItems: ChecklistItem[];
};

type DeviceExpertMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
};

type StepAttachment = {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  dataUrl?: string;
  fileUrl?: string;
  storageKey?: string;
  storageProvider?: string | null;
  addedAt: string;
  uploadedAt?: string;
  uploadedByUserId?: string | null;
  uploadedByName?: string | null;
};

type AttachmentViewerState = {
  url: string;
  fileName?: string | null;
  fileType?: string | null;
  mimeType?: string | null;
  fileSize?: number | string | null;
};

type StepMetadata = {
  checklist?: ChecklistItem[];
  allowUploads?: boolean;
  allowNotes?: boolean;
  allowSkip?: boolean;
  skipped?: boolean;
  attachments?: StepAttachment[];
  repackAcknowledged?: boolean;
  layout?: 'site' | 'compact' | 'standard';
  attachmentLabel?: string | null;
  attachmentHint?: string | null;
  notApplicable?: boolean;
  notApplicableReason?: string | null;
  notApplicableNote?: string | null;
  completionState?: 'COMPLETE' | 'PARTIAL' | 'INCOMPLETE';
  referenceImages?: ReferenceImageHit[];
  referenceImageBlocks?: ReferenceImageBlock[];
};

type PrepStep = {
  id: string;
  stepKey: string;
  label: string;
  status: string;
  sequence: number;
  notes?: string | null;
  metadata: StepMetadata;
};

type PreInstallStatusState =
  | 'COMPLETE'
  | 'NOT_REQUIRED'
  | 'INCOMPLETE'
  | 'INCOMPLETE_WITH_REASON'
  | 'PARTIAL'
  | 'UNKNOWN';

type PreInstallStatusSummary = {
  state: PreInstallStatusState;
  outstandingSteps?: string[];
  note?: string | null;
  lastUpdatedAt?: string | null;
  summary?: string | null;
  reportGeneratedAt?: string | null;
  serviceReport?: ServiceReportRecord | null;
  notRequired?: boolean;
  requirementOverride?: Record<string, unknown> | null;
  referenceRevisionId?: string | null;
  sourceTaskId?: string | null;
  runId?: string | null;
};

type CompletionIssue = {
  stepId: string;
  stepLabel: string;
  itemLabels: string[];
};

type PhaseMeta = {
  preInstallStatus?: PreInstallStatusSummary | null;
  installExecutionStatus?: PreInstallStatusSummary | null;
};

type ProtocolAvailability = {
  available?: boolean;
  isActive?: boolean;
  canContinue?: boolean;
  reason?: string | null;
  message?: string | null;
  referenceType?: string | null;
  modelId?: string | null;
  modelNumber?: string | null;
  modelName?: string | null;
  revisionId?: string | null;
  hasSavedRun?: boolean;
  activeStepCount?: number;
};

type Workspace = {
  case: {
    id: string;
    caseCode?: string | null;
    caseNumber?: string | null;
    problemDescription?: string;
    customer?: {
      name?: string | null;
      contact?: {
        name?: string | null;
        email?: string | null;
        phone?: string | null;
      } | null;
    } | null;
  device?: {
    serialNumber?: string | null;
    modelId?: string | null;
    modelNumber?: string | null;
    modelName?: string | null;
  } | null;
  } | null;
  task: {
    id: string;
    status: string;
    taskCode?: string | null;
    stage?: { label?: string | null } | null;
  } | null;
  run: {
    id: string;
    status: string;
    notes?: string | null;
    metadata?: Record<string, unknown>;
  } | null;
  phaseMeta?: PhaseMeta | null;
  protocolAvailability?: ProtocolAvailability | null;
  serviceReport?: ServiceReportRecord | null;
  serviceReportTemplate?: ReportTemplate | null;
  steps: PrepStep[];
  access?: {
    canWrite?: boolean;
    assignedUserId?: string | null;
    assignedUserName?: string | null;
  } | null;
};

const MAX_ATTACHMENTS = 6;
const MAX_ATTACHMENT_BYTES = 6 * 1024 * 1024;

const BRAND_TEXT = '#123F43';
const BRAND_MUTED = '#64716F';
const BRAND_SURFACE = '#F2EEE7';
const BRAND_SURFACE_SOFT = '#FBF8F2';
const BRAND_BORDER = '#DDD4C8';
const BRAND_CARD = '#FFFDF9';
const BRAND_TEXT_SOFT = 'rgba(18, 63, 67, 0.08)';
const BRAND_TEXT_SOFT_BORDER = 'rgba(18, 63, 67, 0.18)';
const BRAND_ACCENT = '#D99A33';
const BRAND_ACCENT_SOFT = '#F6E7C9';
const BRAND_ACCENT_TEXT = '#8A611E';
const BRAND_SUCCESS_SOFT = '#E7F2EC';
const BRAND_SUCCESS_BORDER = '#BDD4C7';
const BRAND_SUCCESS_TEXT = '#25624D';
const BRAND_DANGER_SOFT = '#FDEDEC';
const BRAND_DANGER_BORDER = '#E7B9B4';
const BRAND_DANGER_TEXT = '#A63B32';
const MEASUREMENT_CAPTURE_ENABLED = true;

function AuthenticatedReferenceImage({
  src,
  alt,
  style,
  placeholderStyle,
  placeholderText = 'No preview available'
}: {
  src?: string | null;
  alt: string;
  style: React.CSSProperties;
  placeholderStyle: React.CSSProperties;
  placeholderText?: string;
}) {
  const [resolvedSrc, setResolvedSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!src) {
      setResolvedSrc(null);
      setFailed(false);
      return undefined;
    }

    if (/^(?:data|blob):/i.test(src)) {
      setResolvedSrc(src);
      setFailed(false);
      return undefined;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    setResolvedSrc(null);
    setFailed(false);

    const fetchImage = async () => {
      const token = getAuthToken();
      const absoluteHttpUrl = /^https?:/i.test(src);
      const baseUrl = await getBaseUrl().catch(() => '');
      const requestUrl =
        baseUrl && !absoluteHttpUrl
          ? `${baseUrl.replace(/\/+$/, '')}${src.startsWith('/') ? src : `/${src}`}`
          : src;
      const sameAppUrl =
        baseUrl && requestUrl.startsWith(baseUrl.replace(/\/+$/, ''));
      const response = await fetch(requestUrl, {
        headers: token && (!absoluteHttpUrl || sameAppUrl) ? { Authorization: `Bearer ${token}` } : undefined
      });
      if (!response.ok) throw new Error('Image unavailable');
      return response.blob();
    };

    fetchImage()
      .then(blob => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setResolvedSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (!src) {
    return <div style={placeholderStyle}>{placeholderText}</div>;
  }

  if (failed) {
    return <div style={placeholderStyle}>Image unavailable</div>;
  }

  if (!resolvedSrc) {
    return <div style={placeholderStyle}>Loading image...</div>;
  }

  return <img src={resolvedSrc} alt={alt} style={style} />;
}

const normalizeWhitespace = (value: string) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

const dedupeReferenceImages = (images: ReferenceImageHit[] = []) => {
  const byId = new Map<string, ReferenceImageHit>();
  images.forEach(image => {
    const imageId = String(image?.id || '').trim();
    if (!imageId || byId.has(imageId)) return;
    byId.set(imageId, image);
  });
  return Array.from(byId.values());
};

const formatAttachmentSize = (bytes: number | string | null | undefined): string => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '';
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  if (value >= 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${Math.round(value)} B`;
};

const getAttachmentDisplayUrl = (attachment: StepAttachment) =>
  String(attachment?.fileUrl || attachment?.dataUrl || '').trim();

const isImageAttachment = (attachment: StepAttachment) => {
  const mimeType = String(attachment?.mimeType || '').toLowerCase();
  const name = String(attachment?.name || '').toLowerCase();
  return mimeType.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name);
};

const toAttachmentPreviewImage = (attachment: StepAttachment): ReferenceImageHit => ({
  id: attachment.id,
  label: attachment.name || 'Task attachment',
  hint: formatAttachmentSize(attachment.size),
  fileUrl: getAttachmentDisplayUrl(attachment)
});

const buildAttachmentPreviewGallery = (attachments: StepAttachment[]) =>
  attachments
    .filter(attachment => isImageAttachment(attachment) && getAttachmentDisplayUrl(attachment))
    .map(toAttachmentPreviewImage);

const formatHandoffSummaryText = (value?: string | null) => {
  const original = String(value || '').trim();
  if (!original) return '';
  if (original.includes('\n')) return original;
  const text = normalizeWhitespace(original);
  if (!text) return '';
  return text
    .replace(/\s+-\s+(?=(Confirmed|Recorded|Verified|Validated|Completed|Required|Equipment|Exceptions|Outstanding|Readiness|Installation|Verification|Measurements|Handoff)\b)/g, '\n')
    .replace(/\b(Summary of work done|Readiness outcome|Installation outcome|Verification outcome|Handoff outcome|Exceptions or outstanding items)\s+-\s+/g, '$1\n')
    .trim();
};

const findNonOverlappingPhraseRange = (
  text: string,
  phrase: string,
  occupied: Array<{ start: number; end: number }>
) => {
  const source = String(text || '');
  const target = String(phrase || '').trim();
  if (!source || !target) return null;
  const lowerSource = source.toLowerCase();
  const lowerTarget = target.toLowerCase();
  let fromIndex = 0;
  while (fromIndex < lowerSource.length) {
    const start = lowerSource.indexOf(lowerTarget, fromIndex);
    if (start < 0) return null;
    const end = start + target.length;
    const overlaps = occupied.some(range => start < range.end && end > range.start);
    if (!overlaps) return { start, end };
    fromIndex = start + 1;
  }
  return null;
};

const toDisplayText = (value: number | string | null | undefined): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value.toString();
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  return null;
};

const toNumeric = (value?: number | string | null): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const match = value.match(/-?\d+(?:\.\d+)?/);
    if (match) {
      const parsed = Number(match[0]);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
};

type CaptureUiMode = 'hidden' | 'numeric';

const getCaptureUiMode = (capture: StepCapture): CaptureUiMode => {
  const low = toNumeric(capture.expectedLow);
  const high = toNumeric(capture.expectedHigh);
  const target = toNumeric(capture.target);
  const tolerance = toNumeric(capture.tolerance);
  const hasBoundRange =
    (low !== null && high !== null && Math.abs(low - high) > 1e-4) ||
    (low !== null && high === null) ||
    (low === null && high !== null);
  const hasTargetToleranceRange =
    target !== null && tolerance !== null && tolerance > 0;
  return hasBoundRange || hasTargetToleranceRange ? 'numeric' : 'hidden';
};

const formatMeasurementPlaceholder = (capture: StepCapture): string => {
  return capture.units ? `Enter value (${capture.units})` : 'Enter measured value';
};

const hasMeasurementCaptureValue = (rawValue: string): boolean => rawValue.trim().length > 0;

const formatMeasurementHint = (capture: StepCapture): string | null => {
  const mode = getCaptureUiMode(capture);
  const details: string[] = [];
  const low = toDisplayText(capture.expectedLow);
  const high = toDisplayText(capture.expectedHigh);
  if (mode === 'numeric' && low && high) {
    const unitSuffix = capture.units ? ` ${capture.units}` : '';
    details.push(`Range ${low}${unitSuffix} - ${high}${unitSuffix}`);
  }
  if (capture.method) {
    details.push(`Method: ${capture.method}`);
  }

  if (details.length === 0) return null;
  return details.join(' | ');
};

const determineCompletionState = (metadata: StepMetadata = {}): 'COMPLETE' | 'PARTIAL' | 'INCOMPLETE' => {
  if (metadata.notApplicable) {
    return 'COMPLETE';
  }
  const checklist = Array.isArray(metadata.checklist) ? metadata.checklist : [];
  if (checklist.length === 0) return 'INCOMPLETE';
  const completed = checklist.filter(item => item.completed).length;
  if (completed === 0) return 'INCOMPLETE';
  if (completed === checklist.length) return 'COMPLETE';
  return 'PARTIAL';
};

const buildCompletionIssues = (steps: PrepStep[]): CompletionIssue[] =>
  steps
    .filter(step => !step.metadata?.notApplicable && step.metadata?.completionState !== 'COMPLETE')
    .map(step => {
      const checklist = Array.isArray(step.metadata?.checklist) ? step.metadata.checklist : [];
      const itemLabels = checklist
        .filter(item => !item.completed)
        .map(item => {
          const label = normalizeWhitespace(item.label || 'Checklist item');
          const capture = item.capture;
          const captureValue = String(item.captureValue || '').trim();
          if (MEASUREMENT_CAPTURE_ENABLED && capture?.type === 'MEASUREMENT') {
            if (!hasMeasurementCaptureValue(captureValue)) {
              return `${label} - measured value not entered`;
            }
            return `${label} - measured value entered but item not marked complete`;
          }
          return `${label} - not marked complete`;
        });
      return {
        stepId: step.id,
        stepLabel: step.label,
        itemLabels: itemLabels.length > 0 ? itemLabels : ['Step has no completed checklist evidence']
      };
    });

const parseChecklistLabel = (label: string): { number: string; description: string } => {
  const trimmed = String(label || '').trim().replace(/\s+/g, ' ');
  const match = trimmed.match(/^(\d+(?:\.\d+)*)\s+(.*)$/);
  if (match) {
    return {
      number: match[1].trim(),
      description: match[2].trim()
    };
  }
  return { number: '', description: trimmed };
};

const composeChecklistItemText = (item: ChecklistItem): string => {
  const label = normalizeWhitespace(item.label || '');
  const spec = normalizeWhitespace(item.spec || '');
  if (label && spec) return `${label}: ${spec}`;
  return label || spec || '';
};

const buildDeviceExpertContextOptions = (
  items: ChecklistItem[],
  stepId: string
): DeviceExpertContextOption[] =>
  items.map(item => {
    const parsed = parseChecklistLabel(item.label);
    return {
      key: `${stepId}:${item.id}`,
      stepId,
      number: parsed.number,
      description: parsed.description || item.label,
      label: item.label
    };
  });

const formatDeviceExpertText = (value: string): string => {
  const text = String(value || '').replace(/\r\n/g, '\n').trim();
  if (!text) return '';
  const withBreaks = text
    .replace(/\s+(\d+\))/g, '\n$1')
    .replace(/\s+-\s+/g, '\n- ');
  return withBreaks.replace(/\n{3,}/g, '\n\n').trim();
};

type DocumentationFeedbackForm = {
  feedbackType: string;
  severity: string;
  title: string;
  description: string;
  impact: string;
};

const createFeedbackFormDefaults = (): DocumentationFeedbackForm => ({
  feedbackType: 'WRONG_PROCEDURE',
  severity: 'MEDIUM',
  title: '',
  description: '',
  impact: ''
});

const FEEDBACK_TYPE_OPTIONS = [
  { value: 'WRONG_PROCEDURE', label: 'Incorrect Procedure' },
  { value: 'MISSING_STEP', label: 'Missing Step' },
  { value: 'UNCLEAR_INSTRUCTION', label: 'Unclear Instructions' },
  { value: 'WRONG_PARTS', label: 'Incorrect Parts/Materials' },
  { value: 'SAFETY_CONCERN', label: 'Safety Issue' }
];

const FEEDBACK_SEVERITY_OPTIONS = [
  { value: 'LOW', label: 'Low - Minor inconvenience' },
  { value: 'MEDIUM', label: 'Medium - Time wasted' },
  { value: 'HIGH', label: 'High - Significant impact' },
  { value: 'CRITICAL', label: 'Critical - Safety risk' }
];

function buildProtocolWarningKey(payload: Workspace): string | null {
  const availability = payload.protocolAvailability;
  if (!availability || availability.available !== false) return null;
  return [
    payload.case?.id || 'case',
    payload.run?.id || 'run',
    availability.referenceType || 'protocol',
    availability.reason || 'unavailable',
    availability.revisionId || ''
  ].join('|');
}

function buildProtocolWarningMessage(availability: ProtocolAvailability): string {
  const base =
    availability.message ||
    'No active protocol is available for this task. You can continue manually, or close the task and activate a protocol in Documents.';
  const savedRunText =
    availability.hasSavedRun || Number(availability.activeStepCount || 0) > 0
      ? 'Saved checklist/progress from an earlier task run may still be shown. Continuing will not reload inactive protocol content.'
      : 'No checklist steps were loaded. You can still record notes/status and save manual progress.';
  return `${base}\n\n${savedRunText}`;
}

const NOT_APPLICABLE_OPTIONS = [
  {
    value: 'NOT_REQUIRED',
    label: 'Not required for this installation',
    helper: 'Requirement waived because conditions already meet final state.'
  },
  {
    value: 'CUSTOMER_REQUESTED',
    label: 'Customer requested omission',
    helper: 'Customer provided written approval to skip this step.'
  },
  {
    value: 'SITE_RESTRICTION',
    label: 'Blocked by site restrictions',
    helper: 'Physical site or facility constraint prevents this step.'
  },
  {
    value: 'DEVICE_VARIANT',
    label: 'Different device configuration',
    helper: 'This unit ships with an alternative configuration that removes this need.'
  },
  {
    value: 'OTHER',
    label: 'Other reason',
    helper: ''
  }
];

type StatusKey = 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED';

type InstallPhaseApi = {
  get(caseId: string): Promise<Workspace>;
  save(
    caseId: string,
    payload: {
      taskId: string;
      runId: string;
      taskStatus?: string;
      runNotes?: string;
      runMetadata?: Record<string, unknown>;
      steps: any[];
    }
  ): Promise<any>;
  refresh(caseId: string): Promise<Workspace>;
  generateReport(caseId: string): Promise<{ summary: string; generatedAt?: string; isCustom?: boolean }>;
  saveReport(caseId: string, summary: string): Promise<{ summary: string; generatedAt?: string; isCustom?: boolean }>;
  createServiceReportDraft(caseId: string): Promise<any>;
  saveServiceReport(caseId: string, document: Record<string, unknown>): Promise<any>;
  sendServiceReport(caseId: string, reportId: string): Promise<any>;
  uploadAttachment(caseId: string, stepId: string, file: File): Promise<{ attachment: StepAttachment }>;
  removeAttachment(caseId: string, stepId: string, attachmentId: string): Promise<any>;
};

type InstallPhaseCopy = {
  caseLabel: string;
  totalTimeLabel: string;
  headerStatus: Record<StatusKey, string>;
  statusButtonLabels: Record<StatusKey, string>;
  summaryPlaceholder: string;
  summaryGenerateLabel: string;
  summaryGeneratingLabel: string;
  summaryEditLabel: string;
  summaryNoDataLabel: string;
  runNotesLabel: string;
  markCompleteButtonLabel: string;
  loadingMessage: string;
  loadError: string;
  saveError: string;
};

type PreInstallCheckCopy = {
  title: string;
  description?: string;
  statusKey?: 'preInstallStatus' | 'installExecutionStatus';
  badgeLabels: Record<PreInstallStatusState, string>;
  badgeColors?: Partial<Record<PreInstallStatusState, { bg: string; color: string }>>;
  outstandingTitle?: string;
  noteLabel?: string;
  actionLabel?: string;
  actionPath?: 'pre' | 'exec' | 'verify' | 'decom';
  completeActionLabel?: string;
  openActionLabel?: string;
  notRequiredDescription?: string;
  readyDescription?: string;
  requirementOverrideMetadataKey?: string;
  notRequiredConfirmTitle?: string;
  notRequiredConfirmMessage?: string;
  notRequiredNote?: string;
  requireActionLabel?: string;
};

type InstallPhaseConfig = {
  phase: 'PREP' | 'EXECUTION' | 'VERIFICATION' | 'DECOMMISSION';
  api: InstallPhaseApi;
  copy: InstallPhaseCopy;
  preInstallCheck?: PreInstallCheckCopy;
  enforceLayout?: 'site' | 'compact' | 'standard';
  disableStepNotes?: boolean;
};

type InstallPhaseWorkspaceProps = {
  config: InstallPhaseConfig;
};

const PRE_INSTALL_STATE_COLORS: Record<
  PreInstallStatusState,
  { bg: string; color: string }
> = {
  COMPLETE: { bg: BRAND_SUCCESS_SOFT, color: BRAND_SUCCESS_TEXT },
  NOT_REQUIRED: { bg: BRAND_SUCCESS_SOFT, color: BRAND_SUCCESS_TEXT },
  INCOMPLETE: { bg: BRAND_DANGER_SOFT, color: BRAND_DANGER_TEXT },
  INCOMPLETE_WITH_REASON: { bg: BRAND_ACCENT_SOFT, color: BRAND_ACCENT_TEXT },
  PARTIAL: { bg: BRAND_TEXT_SOFT, color: BRAND_TEXT },
  UNKNOWN: { bg: BRAND_SURFACE_SOFT, color: BRAND_MUTED }
};

const InstallPhaseWorkspace: React.FC<InstallPhaseWorkspaceProps> = ({ config }) => {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();

  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [phaseMeta, setPhaseMeta] = useState<PhaseMeta | null>(null);
  const [steps, setSteps] = useState<PrepStep[]>([]);
  const [runNotes, setRunNotes] = useState('');
  const [totalTimeHours, setTotalTimeHours] = useState('');
  const [taskStatus, setTaskStatus] = useState<'IN_PROGRESS' | 'COMPLETED' | 'ON_HOLD'>('IN_PROGRESS');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [referenceRevisionId, setReferenceRevisionId] = useState<string | null>(null);
  const [collapsedSteps, setCollapsedSteps] = useState<Record<string, boolean>>({});
  const [feedbackModal, setFeedbackModal] = useState<{ stepId: string; stepLabel: string } | null>(
    null
  );
  const [feedbackForm, setFeedbackForm] = useState<DocumentationFeedbackForm>(
    createFeedbackFormDefaults()
  );
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState<string | null>(null);
  const [reportSummary, setReportSummary] = useState<string | null>(null);
  const [reportGeneratedAt, setReportGeneratedAt] = useState<string | null>(null);
  const [reportIsCustom, setReportIsCustom] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportEditing, setReportEditing] = useState(false);
  const [reportDraft, setReportDraft] = useState('');
  const [serviceReportModalOpen, setServiceReportModalOpen] = useState(false);
  const [serviceReportDraft, setServiceReportDraft] = useState<ServiceReportDocument | null>(null);
  const [serviceReportLoading, setServiceReportLoading] = useState(false);
  const [serviceReportSaving, setServiceReportSaving] = useState(false);
  const [serviceReportSending, setServiceReportSending] = useState(false);
  const [serviceReportError, setServiceReportError] = useState<string | null>(null);
  const [serviceReportMode, setServiceReportMode] = useState<'create' | 'view'>('create');
  const [reportEditError, setReportEditError] = useState<string | null>(null);
  const [preInstallDetailsOpen, setPreInstallDetailsOpen] = useState(false);
  const [deviceExpertOpen, setDeviceExpertOpen] = useState(true);
  const [deviceExpertStepId, setDeviceExpertStepId] = useState('');
  const [deviceExpertContextKey, setDeviceExpertContextKey] = useState('');
  const [deviceExpertQuestion, setDeviceExpertQuestion] = useState('');
  const [deviceExpertMessages, setDeviceExpertMessages] = useState<DeviceExpertMessage[]>([]);
  const [deviceExpertLoading, setDeviceExpertLoading] = useState(false);
  const [deviceExpertError, setDeviceExpertError] = useState<string | null>(null);
  const [deviceExpertConversationId, setDeviceExpertConversationId] = useState<string | null>(null);
  const [previewImages, setPreviewImages] = useState<ReferenceImageHit[]>([]);
  const [previewImageIndex, setPreviewImageIndex] = useState(0);
  const [previewZoom, setPreviewZoom] = useState(100);
  const [attachmentViewer, setAttachmentViewer] = useState<AttachmentViewerState | null>(null);
  const [attachmentUploadingStepId, setAttachmentUploadingStepId] = useState<string | null>(null);
  const [attachmentRemovingId, setAttachmentRemovingId] = useState<string | null>(null);
  const [captureDetailsOpen, setCaptureDetailsOpen] = useState<Record<string, boolean>>({});
  const [completionGateIssues, setCompletionGateIssues] = useState<CompletionIssue[] | null>(null);
  const [forceCompletionReason, setForceCompletionReason] = useState('');
  const [forceCompletionError, setForceCompletionError] = useState<string | null>(null);
  const protocolWarningKeyRef = useRef<string | null>(null);
  const workspaceReadOnly = workspace?.access?.canWrite === false;
  const savedServiceReportStatus = String(workspace?.serviceReport?.status || '').trim().toUpperCase();
  const savedServiceReportSent = savedServiceReportStatus === 'SENT';
  const canRebuildServiceReport =
    Boolean(reportSummary?.trim()) &&
    !workspaceReadOnly &&
    taskStatus === 'COMPLETED' &&
    !savedServiceReportSent;
  const assignedLabel = workspace?.access?.assignedUserName || workspace?.access?.assignedUserId || null;
  const previewImage = previewImages[previewImageIndex] || null;
  const previewImageCount = previewImages.length;

  const openPreviewImages = useCallback((images: ReferenceImageHit[], imageId?: string) => {
    const gallery = dedupeReferenceImages(images.filter(image => image && image.id));
    if (gallery.length === 0) return;
    const requestedIndex = imageId ? gallery.findIndex(image => image.id === imageId) : 0;
    setPreviewZoom(100);
    setPreviewImageIndex(requestedIndex >= 0 ? requestedIndex : 0);
    setPreviewImages(gallery);
  }, []);

  const closePreviewImages = useCallback(() => {
    setPreviewImages([]);
    setPreviewImageIndex(0);
    setPreviewZoom(100);
  }, []);

  const showPreviousPreviewImage = useCallback(() => {
    setPreviewZoom(100);
    setPreviewImageIndex(index => Math.max(0, index - 1));
  }, []);

  const showNextPreviewImage = useCallback(() => {
    setPreviewZoom(100);
    setPreviewImageIndex(index => Math.min(previewImages.length - 1, index + 1));
  }, [previewImages.length]);

  const selectPreviewImage = useCallback((index: number) => {
    setPreviewZoom(100);
    setPreviewImageIndex(index);
  }, []);

  const toggleCaptureDetails = useCallback((stepId: string, itemId: string) => {
    const key = `${stepId}:${itemId}`;
    setCaptureDetailsOpen(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  const showProtocolWarningIfNeeded = useCallback(
    async (payload: Workspace) => {
      const warningKey = buildProtocolWarningKey(payload);
      const availability = payload.protocolAvailability;
      if (!warningKey || !availability || protocolWarningKeyRef.current === warningKey) {
        return;
      }
      protocolWarningKeyRef.current = warningKey;
      const confirmed = await showInAppConfirm(buildProtocolWarningMessage(availability), {
        title: 'No active protocol available',
        confirmLabel: 'Continue manually',
        cancelLabel: 'Close task',
        tone: 'danger'
      });
      if (!confirmed) {
        navigate(-1);
      }
    },
    [navigate]
  );

  const applyWorkspace = useCallback(
    (payload: Workspace, options: { savedAt?: string | null } = {}) => {
      setWorkspace(payload);
      setPhaseMeta(payload.phaseMeta || null);
      const normalizedSteps = (payload.steps || []).map(step => ({
        ...step,
        metadata: {
          ...step.metadata,
          completionState: determineCompletionState(step.metadata)
        }
      }));
      setSteps(normalizedSteps);
      setRunNotes(payload.run?.notes || '');
      const resolvedStatus =
        typeof payload.task?.status === 'string'
          ? (payload.task.status.toUpperCase() as 'IN_PROGRESS' | 'COMPLETED' | 'ON_HOLD')
          : 'IN_PROGRESS';
      setTaskStatus(resolvedStatus);
      const runMetadata =
        payload.run?.metadata && typeof payload.run.metadata === 'object'
          ? (payload.run.metadata as Record<string, unknown>)
          : {};
      const referenceRevision =
        typeof (runMetadata as any).referenceRevisionId === 'string'
          ? ((runMetadata as any).referenceRevisionId as string)
          : null;
      setReferenceRevisionId(referenceRevision);
      const totalMinutes = Number((runMetadata as any).totalTimeMinutes) || 0;
      setTotalTimeHours(totalMinutes > 0 ? (totalMinutes / 60).toFixed(1) : '');
      const savedTimestamp =
        options.savedAt ??
        (typeof (runMetadata as any).lastUpdatedAt === 'string'
          ? ((runMetadata as any).lastUpdatedAt as string)
          : null);
      setReportSummary(
        typeof (runMetadata as any).reportSummary === 'string'
          ? ((runMetadata as any).reportSummary as string)
          : null
      );
      setReportGeneratedAt(
        typeof (runMetadata as any).reportGeneratedAt === 'string'
          ? ((runMetadata as any).reportGeneratedAt as string)
          : null
      );
      setReportIsCustom(Boolean((runMetadata as any).reportIsCustom));
      setReportEditing(false);
      setReportDraft('');
      setReportError(null);
      setReportEditError(null);
      setLastSavedAt(savedTimestamp);
      setHasUnsavedChanges(false);
      setCollapsedSteps({});
      setPreInstallDetailsOpen(false);
      if (payload.protocolAvailability?.available !== false) {
        protocolWarningKeyRef.current = null;
      }
    },
    []
  );

  const loadWorkspace = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
      setError(null);
    try {
      const result = await config.api.get(caseId);
      applyWorkspace(result);
      void showProtocolWarningIfNeeded(result);
    } catch (err) {
      console.error('installPhase: load failed', err);
      setError(err instanceof Error ? err.message : config.copy.loadError);
    } finally {
      setLoading(false);
    }
  }, [applyWorkspace, caseId, config.api, config.copy.loadError, showProtocolWarningIfNeeded]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  const updateStep = useCallback((stepId: string, updater: (current: PrepStep) => PrepStep) => {
    if (workspaceReadOnly) return;
    setSteps(prev =>
      prev.map(step => {
        if (step.id !== stepId) return step;
        return updater(step);
      })
    );
    setHasUnsavedChanges(true);
  }, [workspaceReadOnly]);

  const toggleChecklistItem = useCallback(
    (stepId: string, itemId: string) => {
      updateStep(stepId, current => {
        const checklist = Array.isArray(current.metadata.checklist)
          ? current.metadata.checklist
          : [];
        const targetItem = checklist.find(item => item.id === itemId) || null;
        if (
          targetItem?.capture?.type === 'MEASUREMENT' &&
          MEASUREMENT_CAPTURE_ENABLED &&
          !targetItem.completed
        ) {
          const mode = getCaptureUiMode(targetItem.capture);
          if (mode === 'numeric' && !hasMeasurementCaptureValue(String(targetItem.captureValue || ''))) {
            return current;
          }
        }
        const updatedMetadata = {
          ...current.metadata,
          checklist: checklist.map(item =>
            item.id === itemId ? { ...item, completed: !item.completed } : item
          )
        };
        return {
          ...current,
          metadata: {
            ...updatedMetadata,
            completionState: determineCompletionState(updatedMetadata)
          }
        };
      });
    },
    [updateStep]
  );

  const handleChecklistCaptureValueChange = useCallback(
    (stepId: string, itemId: string, value: string) => {
      updateStep(stepId, current => {
        const checklist = Array.isArray(current.metadata.checklist)
          ? current.metadata.checklist
          : [];
        const updatedMetadata = {
          ...current.metadata,
          checklist: checklist.map(item =>
            item.id === itemId ? { ...item, captureValue: value } : item
          )
        };
        return {
          ...current,
          metadata: {
            ...updatedMetadata,
            completionState: determineCompletionState(updatedMetadata)
          }
        };
      });
    },
    [updateStep]
  );

  const handleChecklistNoteChange = useCallback(
    (stepId: string, itemId: string, value: string) => {
      updateStep(stepId, current => {
        const checklist = Array.isArray(current.metadata.checklist)
          ? current.metadata.checklist
          : [];
        const updatedMetadata = {
          ...current.metadata,
          checklist: checklist.map(item =>
            item.id === itemId ? { ...item, notes: value } : item
          )
        };
        return {
          ...current,
          metadata: {
            ...updatedMetadata,
            completionState: determineCompletionState(updatedMetadata)
          }
        };
      });
    },
    [updateStep]
  );

  const handleAttachmentAdd = useCallback(
    async (stepId: string, event: ChangeEvent<HTMLInputElement>) => {
      if (workspaceReadOnly) {
        event.target.value = '';
        return;
      }
      if (!caseId) {
        event.target.value = '';
        setError('Cannot upload an attachment without a case.');
        return;
      }
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const fileArray = Array.from(files);
      const existingAttachments = steps.find(step => step.id === stepId)?.metadata.attachments || [];
      let remainingSlots = Math.max(0, MAX_ATTACHMENTS - existingAttachments.length);
      if (remainingSlots <= 0) {
        setError('Maximum attachments reached for this step.');
        event.target.value = '';
        return;
      }
      setError(null);
      setAttachmentUploadingStepId(stepId);
      for (const file of fileArray) {
        if (remainingSlots <= 0) {
          setError(`Only ${MAX_ATTACHMENTS} attachments can be added to one step.`);
          break;
        }
        if (file.size > MAX_ATTACHMENT_BYTES) {
          setError(`"${file.name}" exceeds the 6MB upload limit and was skipped.`);
          continue;
        }
        try {
          const response = await config.api.uploadAttachment(caseId, stepId, file);
          const attachment = response?.attachment;
          if (!attachment?.id) {
            throw new Error('Attachment upload did not return file metadata.');
          }
          setSteps(prev =>
            prev.map(step => {
              if (step.id !== stepId) return step;
              const attachments = Array.isArray(step.metadata.attachments)
                ? step.metadata.attachments
                : [];
              if (attachments.some(existing => existing.id === attachment.id)) return step;
              return {
                ...step,
                metadata: {
                  ...step.metadata,
                  attachments: [...attachments, attachment]
                }
              };
            })
          );
          remainingSlots -= 1;
        } catch (err) {
          console.error('Install task attachment upload failed', err);
          setError(err instanceof Error ? err.message : 'Failed to upload task attachment.');
        }
      }

      event.target.value = '';
      setAttachmentUploadingStepId(null);
    },
    [caseId, config.api, steps, workspaceReadOnly]
  );

  const handleAttachmentRemove = useCallback(
    async (stepId: string, attachmentId: string) => {
      if (workspaceReadOnly) return;
      if (!caseId) {
        setError('Cannot remove an attachment without a case.');
        return;
      }
      setError(null);
      setAttachmentRemovingId(attachmentId);
      try {
        await config.api.removeAttachment(caseId, stepId, attachmentId);
        setSteps(prev =>
          prev.map(step => {
            if (step.id !== stepId) return step;
            const attachments = Array.isArray(step.metadata.attachments)
              ? step.metadata.attachments
              : [];
            return {
              ...step,
              metadata: {
                ...step.metadata,
                attachments: attachments.filter(attachment => attachment.id !== attachmentId)
              }
            };
          })
        );
      } catch (err) {
        console.error('Install task attachment remove failed', err);
        setError(err instanceof Error ? err.message : 'Failed to remove task attachment.');
      } finally {
        setAttachmentRemovingId(null);
      }
    },
    [caseId, config.api, workspaceReadOnly]
  );

  const openStoredAttachment = useCallback(async (attachment: StepAttachment) => {
    const sourceUrl = getAttachmentDisplayUrl(attachment);
    if (!sourceUrl) {
      setError('Attachment file URL is not available.');
      return;
    }
    let targetUrl = sourceUrl;
    if (!/^(?:https?:|data:|blob:)/i.test(sourceUrl)) {
      const baseUrl = await getBaseUrl().catch(() => '');
      if (baseUrl) {
        targetUrl = `${baseUrl.replace(/\/+$/, '')}${sourceUrl.startsWith('/') ? sourceUrl : `/${sourceUrl}`}`;
      }
    }
    setAttachmentViewer({
      url: targetUrl,
      fileName: attachment.name || 'Task attachment',
      mimeType: attachment.mimeType || null,
      fileType: attachment.mimeType || null,
      fileSize: attachment.size || null
    });
  }, []);

  const handleStepNotesChange = useCallback(
    (stepId: string, value: string) => {
      updateStep(stepId, current => ({
        ...current,
        notes: value
      }));
    },
    [updateStep]
  );

  const handleSkipSoftware = useCallback(
    (stepId: string, value: boolean) => {
      updateStep(stepId, current => ({
        ...current,
        metadata: {
          ...current.metadata,
          skipped: value
        }
      }));
    },
    [updateStep]
  );

  const handleRepackAck = useCallback(
    (stepId: string, value: boolean) => {
      updateStep(stepId, current => ({
        ...current,
        metadata: {
          ...current.metadata,
          repackAcknowledged: value
        }
      }));
    },
    [updateStep]
  );

  const handleNotApplicableToggle = useCallback(
    (stepId: string, value: boolean) => {
      updateStep(stepId, current => ({
        ...current,
        metadata: {
          ...current.metadata,
          notApplicable: value,
          notApplicableReason: value ? current.metadata.notApplicableReason || null : null,
          notApplicableNote: value ? current.metadata.notApplicableNote || '' : '',
          completionState: determineCompletionState({
            ...current.metadata,
            notApplicable: value,
            notApplicableReason: value ? current.metadata.notApplicableReason || null : null,
            notApplicableNote: value ? current.metadata.notApplicableNote || '' : ''
          })
        }
      }));
    },
    [updateStep]
  );


  const handleNotApplicableReasonChange = useCallback(
    (stepId: string, reason: string) => {
      const helper =
        NOT_APPLICABLE_OPTIONS.find(option => option.value === reason)?.helper || '';
      updateStep(stepId, current => {
        const existingNote = current.metadata.notApplicableNote || '';
        const nextNote =
          helper && existingNote.trim().length === 0 ? helper : existingNote;
        return {
          ...current,
          metadata: {
            ...current.metadata,
            notApplicableReason: reason,
            notApplicableNote: nextNote
          }
        };
      });
    },
    [updateStep]
  );

  const handleNotApplicableNoteChange = useCallback(
    (stepId: string, value: string) => {
      updateStep(stepId, current => ({
        ...current,
        metadata: {
          ...current.metadata,
          notApplicableNote: value
        }
      }));
    },
    [updateStep]
  );

  const handleOpenFeedbackModal = useCallback((step: PrepStep) => {
    setFeedbackModal({ stepId: step.id, stepLabel: step.label });
    setFeedbackForm(createFeedbackFormDefaults());
    setFeedbackError(null);
    setFeedbackSuccess(null);
  }, []);

  const handleCloseFeedbackModal = useCallback(() => {
    setFeedbackModal(null);
    setFeedbackForm(createFeedbackFormDefaults());
    setFeedbackError(null);
    setFeedbackSuccess(null);
  }, []);

  const handleSubmitFeedback = useCallback(async () => {
    if (!feedbackModal) return;
    const trimmedTitle = feedbackForm.title.trim();
    const trimmedDescription = feedbackForm.description.trim();
    if (!trimmedTitle || !trimmedDescription) {
      setFeedbackError('Provide a short title and detailed description.');
      return;
    }
    const modelId = workspace?.case?.device?.modelId;
    if (!modelId) {
      setFeedbackError('Device model unavailable. Reload the case and try again.');
      return;
    }
    try {
      setFeedbackSubmitting(true);
      setFeedbackError(null);
      setFeedbackSuccess(null);
      await tsfadApi.submitDocumentationFeedback({
        modelId,
        caseId: workspace?.case?.id ?? null,
        feedbackType: feedbackForm.feedbackType,
        severity: feedbackForm.severity,
        title: trimmedTitle,
        description: trimmedDescription,
        impact: feedbackForm.impact.trim() || null,
        technicianId: 'install-prep',
        technicianName: 'Install Technician',
        metadata: {
          source: 'install-prep-task',
          installPrepStepId: feedbackModal.stepId,
          installPrepStepLabel: feedbackModal.stepLabel
        }
      });
      setFeedbackSuccess('Thanks - feedback submitted to documentation.');
      setFeedbackForm(createFeedbackFormDefaults());
    } catch (err) {
      console.error('installPrep: doc feedback failed', err);
      setFeedbackError('Failed to submit documentation feedback.');
    } finally {
      setFeedbackSubmitting(false);
    }
  }, [feedbackForm, feedbackModal, workspace]);

  const validateBeforeSave = useCallback(() => {
    for (const step of steps) {
      if (step.metadata?.notApplicable) {
        if (!step.metadata.notApplicableReason) {
          return `Select a reason for marking "${step.label}" as not applicable.`;
        }
        if (!step.metadata.notApplicableNote || step.metadata.notApplicableNote.trim().length === 0) {
          return `Add a short explanation for "${step.label}" when marking it not applicable.`;
        }
      }
    }
    return null;
  }, [steps]);

  const buildSavePayload = useCallback(
    (runNotesOverride?: string, runMetadataPatch?: Record<string, unknown>) => {
      if (!workspace?.task || !workspace.run) return null;
      const normalizedTotalTime =
        totalTimeHours && !Number.isNaN(Number(totalTimeHours))
          ? Math.round(Number(totalTimeHours) * 60)
          : null;
      const notApplicableSteps = steps
        .filter(step => step.metadata?.notApplicable)
        .map(step => ({
          stepId: step.id,
          stepLabel: step.label,
          reason: step.metadata.notApplicableReason || null,
          reasonLabel:
            NOT_APPLICABLE_OPTIONS.find(option => option.value === step.metadata.notApplicableReason)
              ?.label || null,
          note: step.metadata.notApplicableNote || ''
        }));
      const incompleteSteps = steps
        .filter(step => !step.metadata?.notApplicable && step.metadata?.completionState !== 'COMPLETE')
        .map(step => ({
          stepId: step.id,
          stepLabel: step.label,
          completionState: step.metadata.completionState || 'INCOMPLETE'
        }));
      const existingRunMetadata =
        workspace.run.metadata && typeof workspace.run.metadata === 'object'
          ? workspace.run.metadata
          : {};
      const effectiveRunNotes = runNotesOverride ?? runNotes;
      return {
        taskId: workspace.task.id,
        runId: workspace.run.id,
        taskStatus: taskStatus,
        runNotes: effectiveRunNotes,
        runMetadata: {
          ...existingRunMetadata,
          lastUpdatedAt: new Date().toISOString(),
          ...(normalizedTotalTime !== null ? { totalTimeMinutes: normalizedTotalTime } : {}),
          ...(reportSummary ? { reportSummary } : {}),
          ...(reportGeneratedAt ? { reportGeneratedAt } : {}),
          reportIsCustom: Boolean(reportIsCustom),
          incompleteByDesign: notApplicableSteps.length > 0,
          notApplicableSteps,
          remainingSteps: incompleteSteps,
          ...(runMetadataPatch || {})
        },
        steps: steps.map(step => ({
          id: step.id,
          status: step.status,
          notes: step.notes,
          metadata: step.metadata
        }))
      };
    },
    [reportGeneratedAt, reportIsCustom, reportSummary, runNotes, steps, taskStatus, totalTimeHours, workspace]
  );

  const handleSave = useCallback(
    async (options?: {
      status?: 'IN_PROGRESS' | 'COMPLETED' | 'ON_HOLD';
      forceReason?: string;
      runMetadataPatch?: Record<string, unknown>;
    }) => {
      if (!caseId) return;
      if (workspaceReadOnly) return;
      const validationError = validateBeforeSave();
      if (validationError) {
        setError(validationError);
        return;
      }
      const forceReason = typeof options?.forceReason === 'string' ? options.forceReason.trim() : '';
      if (options?.status === 'COMPLETED') {
        const remaining = buildCompletionIssues(steps);
        if (remaining.length > 0 && !forceReason) {
          setCompletionGateIssues(remaining);
          setForceCompletionReason(runNotes.trim());
          setForceCompletionError(null);
          setError(null);
          return;
        }
      }
      const payload = buildSavePayload(forceReason || undefined, options?.runMetadataPatch);
      if (!payload) return;
      const statusOverride = options?.status;
      if (statusOverride) {
        payload.taskStatus = statusOverride;
      }
      setSaving(true);
      setError(null);
      try {
        const response = await config.api.save(caseId, payload);
        const savedAt = response.savedAt || new Date().toISOString();
        applyWorkspace(response, { savedAt });
      } catch (err) {
        console.error('installPhase: save failed', err);
        setError(err instanceof Error ? err.message : config.copy.saveError);
      } finally {
        setSaving(false);
      }
    },
    [
      applyWorkspace,
      buildSavePayload,
      caseId,
      config.api,
      config.copy.saveError,
      runNotes,
      steps,
      validateBeforeSave,
      workspaceReadOnly
    ]
  );

  const handleCloseCompletionGate = useCallback(() => {
    setCompletionGateIssues(null);
    setForceCompletionReason('');
    setForceCompletionError(null);
  }, []);

  const handleConfirmForcedCompletion = useCallback(() => {
    const trimmedReason = forceCompletionReason.trim();
    if (!trimmedReason) {
      setForceCompletionError('Enter the reason this phase can be closed with incomplete checklist items.');
      return;
    }
    void handleSave({ status: 'COMPLETED', forceReason: trimmedReason });
    setCompletionGateIssues(null);
    setForceCompletionError(null);
  }, [forceCompletionReason, handleSave]);

  const handleGenerateReport = useCallback(async () => {
    if (workspaceReadOnly) return;
    if (!caseId || reportLoading) return;
    if (taskStatus !== 'COMPLETED') return;
    try {
      if (hasUnsavedChanges) {
        await handleSave();
      }
      setReportError(null);
      setReportEditError(null);
      setReportEditing(false);
      setReportDraft('');
      setReportLoading(true);
      const response = await config.api.generateReport(caseId);
      const summary =
        typeof response?.summary === 'string' ? response.summary.trim() : '';
      setReportSummary(summary || null);
      setReportGeneratedAt(
        typeof response?.generatedAt === 'string'
          ? response.generatedAt
          : new Date().toISOString()
      );
      setReportIsCustom(Boolean(response?.isCustom));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to generate summary.';
      setReportError(message);
    } finally {
      setReportLoading(false);
    }
  }, [caseId, config.api, handleSave, hasUnsavedChanges, reportLoading, taskStatus, workspaceReadOnly]);

  const handleOpenPhaseHandoff = useCallback(() => {
    if (!caseId) return;
    const targetPath = config.preInstallCheck?.actionPath || 'pre';
    navigate(`/install/${encodeURIComponent(caseId)}/${targetPath}`);
  }, [caseId, config.preInstallCheck?.actionPath, navigate]);

  const handleMarkPreInstallNotRequired = useCallback(async () => {
    if (workspaceReadOnly || !caseId || saving || !config.preInstallCheck) return;
    const overrideKey =
      config.preInstallCheck.requirementOverrideMetadataKey || 'preInstallRequirementOverride';
    const confirmed = await showInAppConfirm(
      config.preInstallCheck.notRequiredConfirmMessage ||
        'Mark pre-install as not required for this installation? This does not complete the pre-install task; it records that execution can continue without it.',
      {
        title: config.preInstallCheck.notRequiredConfirmTitle || 'Pre-install not required',
        confirmLabel: 'Mark not required',
        cancelLabel: 'Cancel'
      }
    );
    if (!confirmed) return;
    const setAt = new Date().toISOString();
    await handleSave({
      runMetadataPatch: {
        [overrideKey]: {
          status: 'NOT_REQUIRED',
          note:
            config.preInstallCheck.notRequiredNote ||
            'Pre-install marked not required for this installation.',
          setAt
        }
      }
    });
  }, [caseId, config.preInstallCheck, handleSave, saving, workspaceReadOnly]);

  const handleRequirePreInstall = useCallback(async () => {
    if (workspaceReadOnly || !caseId || saving || !config.preInstallCheck) return;
    const overrideKey =
      config.preInstallCheck.requirementOverrideMetadataKey || 'preInstallRequirementOverride';
    await handleSave({
      runMetadataPatch: {
        [overrideKey]: null
      }
    });
  }, [caseId, config.preInstallCheck, handleSave, saving, workspaceReadOnly]);

  const handleStartReportEdit = useCallback(() => {
    if (workspaceReadOnly || taskStatus !== 'COMPLETED' || !reportSummary) return;
    setReportEditError(null);
    setReportEditing(true);
    setReportDraft(reportSummary || '');
  }, [reportSummary, taskStatus, workspaceReadOnly]);

  const handleCancelReportEdit = useCallback(() => {
    setReportEditError(null);
    setReportEditing(false);
    setReportDraft('');
  }, []);

  const handleSaveReportEdit = useCallback(async () => {
    if (workspaceReadOnly) return;
    if (!caseId) return;
    if (taskStatus !== 'COMPLETED') return;
    const trimmed = reportDraft.trim();
    if (!trimmed) {
      setReportEditError('Enter summary text before saving.');
      return;
    }
    try {
      setReportEditError(null);
      setReportLoading(true);
      const response = await config.api.saveReport(caseId, trimmed);
      setReportSummary(response?.summary || trimmed);
      setReportGeneratedAt(
        typeof response?.generatedAt === 'string'
          ? response.generatedAt
          : new Date().toISOString()
      );
      setReportIsCustom(Boolean(response?.isCustom ?? true));
      setReportEditing(false);
      setReportDraft('');
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to save summary.';
      setReportEditError(message);
    } finally {
      setReportLoading(false);
    }
  }, [caseId, config.api, reportDraft, taskStatus, workspaceReadOnly]);

  const handleCreateServiceReport = useCallback(async () => {
    if (!caseId || workspaceReadOnly || serviceReportLoading || serviceReportSaving || serviceReportSending) return;
    if (savedServiceReportSent) {
      setServiceReportError('This service report has already been sent and cannot be regenerated.');
      return;
    }
    if (taskStatus !== 'COMPLETED') {
      setServiceReportError('Complete this install phase before creating a service report.');
      return;
    }
    if (!reportSummary?.trim()) {
      setServiceReportError('Generate and review the customer summary before creating a service report.');
      return;
    }
    try {
      setServiceReportLoading(true);
      setServiceReportError(null);
      const response = (await config.api.createServiceReportDraft(caseId)) as {
        document?: ServiceReportDocument | null;
        existingReport?: ServiceReportRecord | null;
      };
      const document = response?.document || null;
      if (!document) {
        throw new Error('Service report draft was not returned by the server.');
      }
      if (response?.existingReport) {
        setWorkspace(prev => (prev ? { ...prev, serviceReport: response.existingReport || null } : prev));
      }
      setServiceReportMode('create');
      setServiceReportDraft(document);
      setServiceReportModalOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create service report draft.';
      setServiceReportError(message);
      showInAppAlert(message, { title: 'Service report failed', tone: 'danger' });
    } finally {
      setServiceReportLoading(false);
    }
  }, [caseId, config.api, reportSummary, savedServiceReportSent, serviceReportLoading, serviceReportSaving, serviceReportSending, taskStatus, workspaceReadOnly]);

  const handleViewServiceReport = useCallback(() => {
    const document = workspace?.serviceReport?.document || null;
    if (!document) return;
    setServiceReportDraft(document);
    setServiceReportError(null);
    setServiceReportMode('view');
    setServiceReportModalOpen(true);
  }, [workspace?.serviceReport?.document]);

  const handleSaveServiceReport = useCallback(async () => {
    if (!caseId || workspaceReadOnly || serviceReportSaving || serviceReportSending || !serviceReportDraft) return;
    if (taskStatus !== 'COMPLETED') return;
    try {
      setServiceReportSaving(true);
      setServiceReportError(null);
      const response = (await config.api.saveServiceReport(caseId, serviceReportDraft as unknown as Record<string, unknown>)) as {
        serviceReport?: ServiceReportRecord | null;
      };
      const savedReport = response?.serviceReport || null;
      if (!savedReport?.document) {
        throw new Error('Service report save did not return a saved report snapshot.');
      }
      setWorkspace(prev => (prev ? { ...prev, serviceReport: savedReport } : prev));
      setServiceReportDraft(savedReport.document);
      setServiceReportMode('view');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save service report.';
      setServiceReportError(message);
      showInAppAlert(message, { title: 'Service report save failed', tone: 'danger' });
    } finally {
      setServiceReportSaving(false);
    }
  }, [caseId, config.api, serviceReportDraft, serviceReportSaving, serviceReportSending, taskStatus, workspaceReadOnly]);

  const handleSendServiceReport = useCallback(async () => {
    if (!caseId || workspaceReadOnly || serviceReportSending) return;
    if (taskStatus !== 'COMPLETED') return;
    const reportId = workspace?.serviceReport?.id || '';
    if (!reportId) {
      const message = 'Save the service report before sending it.';
      setServiceReportError(message);
      showInAppAlert(message, { title: 'Service report send failed', tone: 'danger' });
      return;
    }
    try {
      setServiceReportSending(true);
      setServiceReportError(null);
      const response = (await config.api.sendServiceReport(caseId, reportId)) as {
        serviceReport?: ServiceReportRecord | null;
      };
      const sentReport = response?.serviceReport || null;
      if (!sentReport?.document) {
        throw new Error('Service report send did not return a saved report snapshot.');
      }
      setWorkspace(prev => (prev ? { ...prev, serviceReport: sentReport } : prev));
      setServiceReportDraft(sentReport.document);
      setServiceReportMode('view');
      await promptCaseCloseAfterServiceReport(caseId, {
        triggerTaskId: workspace?.task?.id || null
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send service report.';
      setServiceReportError(message);
      showInAppAlert(message, { title: 'Service report send failed', tone: 'danger' });
    } finally {
      setServiceReportSending(false);
    }
  }, [caseId, config.api, serviceReportSending, taskStatus, workspace?.serviceReport?.id, workspace?.task?.id, workspaceReadOnly]);

  const closeServiceReportModal = useCallback(() => {
    if (serviceReportLoading || serviceReportSaving || serviceReportSending) return;
    setServiceReportModalOpen(false);
    setServiceReportError(null);
  }, [serviceReportLoading, serviceReportSaving, serviceReportSending]);

  const handleRegenerateChecklist = useCallback(async () => {
    if (workspaceReadOnly) return;
    if (!caseId) return;
    setRefreshing(true);
    setError(null);
    try {
      const response = await config.api.refresh(caseId);
      applyWorkspace(response);
      void showProtocolWarningIfNeeded(response);
    } catch (err) {
      console.error('installPhase: refresh failed', err);
      setError(err instanceof Error ? err.message : 'Failed to regenerate checklist');
    } finally {
      setRefreshing(false);
    }
  }, [applyWorkspace, caseId, config.api, showProtocolWarningIfNeeded, workspaceReadOnly]);

  const caseDisplay = useMemo(() => {
    if (!workspace?.case) return null;
    const id = workspace.case.caseCode || workspace.case.caseNumber || workspace.case.id;
    const customer = workspace.case.customer?.name || 'Customer';
    const deviceModel =
      workspace.case.device?.modelName ||
      workspace.case.device?.modelNumber ||
      'Unspecified model';
    const serial = workspace.case.device?.serialNumber || 'Unknown serial';
    const device = serial ? `${deviceModel} (Serial ${serial})` : deviceModel;
    const contact = workspace.case.customer?.contact?.name || 'Not provided';
    const problemDescription =
      typeof workspace.case.problemDescription === 'string' && workspace.case.problemDescription.trim().length > 0
        ? workspace.case.problemDescription.trim()
        : 'No problem description provided.';
    return { id, customer, deviceModel, serial, device, contact, problemDescription };
  }, [workspace]);

  const headerStatusLabel = useMemo(() => {
    return config.copy.headerStatus[taskStatus] || config.copy.headerStatus.IN_PROGRESS;
  }, [config.copy.headerStatus, taskStatus]);

  const statusChipStyle = useMemo<React.CSSProperties>(() => {
    if (taskStatus === 'COMPLETED') {
      return {
        ...styles.statusChip,
        background: BRAND_SUCCESS_SOFT,
        color: BRAND_SUCCESS_TEXT,
        border: `1px solid ${BRAND_SUCCESS_BORDER}`
      };
    }
    if (taskStatus === 'ON_HOLD') {
      return {
        ...styles.statusChip,
        background: BRAND_ACCENT_SOFT,
        color: BRAND_ACCENT_TEXT,
        border: `1px solid ${BRAND_ACCENT}`
      };
    }
    return {
      ...styles.statusChip,
      background: BRAND_TEXT_SOFT,
      color: BRAND_TEXT,
      border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`
    };
  }, [taskStatus]);

  const completionProgress = useMemo(() => {
    const total = steps.length;
    const completed = steps.filter(step => step.metadata?.completionState === 'COMPLETE').length;
    return { completed, total };
  }, [steps]);

  const deviceExpertModelId = workspace?.case?.device?.modelId || null;
  const deviceExpertParentOptions = useMemo<DeviceExpertParentOption[]>(
    () =>
      steps
        .map(step => ({
          id: step.id,
          sequence: step.sequence,
          label: step.label,
          checklistItems: Array.isArray(step.metadata?.checklist) ? step.metadata.checklist : []
        }))
        .filter(option => option.checklistItems.length > 0),
    [steps]
  );
  const selectedDeviceExpertParent = useMemo(
    () => deviceExpertParentOptions.find(option => option.id === deviceExpertStepId) || null,
    [deviceExpertParentOptions, deviceExpertStepId]
  );
  const deviceExpertContextOptions = useMemo(
    () =>
      selectedDeviceExpertParent
        ? buildDeviceExpertContextOptions(
            selectedDeviceExpertParent.checklistItems,
            selectedDeviceExpertParent.id
          )
        : [],
    [selectedDeviceExpertParent]
  );
  const selectedDeviceExpertContext = useMemo(
    () => deviceExpertContextOptions.find(option => option.key === deviceExpertContextKey) || null,
    [deviceExpertContextKey, deviceExpertContextOptions]
  );

  const referenceLabel = referenceRevisionId ? `Reference ${referenceRevisionId}` : 'Ad-hoc checklist';

  useEffect(() => {
    if (!selectedDeviceExpertParent && deviceExpertStepId) {
      setDeviceExpertStepId('');
    }
  }, [deviceExpertStepId, selectedDeviceExpertParent]);

  useEffect(() => {
    if (
      deviceExpertContextKey &&
      !deviceExpertContextOptions.some(option => option.key === deviceExpertContextKey)
    ) {
      setDeviceExpertContextKey('');
    }
  }, [deviceExpertContextKey, deviceExpertContextOptions]);

  const askDeviceExpert = useCallback(async () => {
    if (!deviceExpertModelId) {
      setDeviceExpertError('Device model context is unavailable for this installation task.');
      return;
    }

    const customQuestion = deviceExpertQuestion.trim();
    const quickQuestion = selectedDeviceExpertContext
      ? `How do I carry out installation checklist item ${
          selectedDeviceExpertContext.number || selectedDeviceExpertContext.description
        } safely and correctly?`
      : '';
    const technicianQuestion = customQuestion || quickQuestion;

    if (!technicianQuestion) {
      setDeviceExpertError('Select a checklist item or enter a question.');
      return;
    }

    try {
      setDeviceExpertLoading(true);
      setDeviceExpertError(null);
      const base = await getBaseUrl();
      const token = getAuthToken();
      const condensedHistory = deviceExpertMessages.slice(-6).map(message => ({
        role: message.role,
        content: message.content
      }));
      const stepContext =
        selectedDeviceExpertParent && selectedDeviceExpertContext
          ? `${config.copy.caseLabel} step ${selectedDeviceExpertParent.sequence}: ${
              selectedDeviceExpertParent.label
            } | Checklist item ${
              selectedDeviceExpertContext.number || selectedDeviceExpertContext.label
            }: ${selectedDeviceExpertContext.description}`
          : selectedDeviceExpertParent
          ? `${config.copy.caseLabel} step ${selectedDeviceExpertParent.sequence}: ${
              selectedDeviceExpertParent.label
            }`
          : null;
      const formattedQuestion = stepContext
        ? [`Focus step: ${stepContext}`, `Technician question: ${technicianQuestion}`].join('\n')
        : technicianQuestion;

      const response = await fetch(`${base}/api/knowledge-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          modelId: deviceExpertModelId,
          question: formattedQuestion,
          history: condensedHistory,
          conversationId: deviceExpertConversationId,
          includeFundamentals: false,
          includeImages: false,
          responseLanguage: 'EN'
        })
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || 'Failed to get Device Expert guidance.');
      }

      const now = new Date().toISOString();
      const assistantTimestamp = payload?.timestamp || now;
      setDeviceExpertMessages(prev => [
        ...prev,
        {
          id: `user-${now}`,
          role: 'user',
          content: technicianQuestion,
          timestamp: now
        },
        {
          id: `assistant-${assistantTimestamp}`,
          role: 'assistant',
          content: String(payload?.answer || '').trim(),
          timestamp: assistantTimestamp
        }
      ]);
      setDeviceExpertConversationId(payload?.conversationId || deviceExpertConversationId || null);
      setDeviceExpertQuestion('');
    } catch (err) {
      console.error('Install task Device Expert failed', err);
      setDeviceExpertError(
        err instanceof Error ? err.message : 'Failed to get Device Expert guidance.'
      );
    } finally {
      setDeviceExpertLoading(false);
    }
  }, [
    config.copy.caseLabel,
    deviceExpertConversationId,
    deviceExpertMessages,
    deviceExpertModelId,
    deviceExpertQuestion,
    selectedDeviceExpertContext,
    selectedDeviceExpertParent
  ]);

  const preInstallCardData = useMemo(() => {
    if (!config.preInstallCheck) return null;
    const statusKey = config.preInstallCheck.statusKey || 'preInstallStatus';
    const status = phaseMeta?.[statusKey] || null;
    const state = (status?.state || 'UNKNOWN') as PreInstallStatusState;
    const badgeLabel =
      config.preInstallCheck.badgeLabels[state] ||
      config.preInstallCheck.badgeLabels.UNKNOWN ||
      state;
    const badgeColors =
      config.preInstallCheck.badgeColors?.[state] ||
      PRE_INSTALL_STATE_COLORS[state] ||
      PRE_INSTALL_STATE_COLORS.UNKNOWN;
    const outstandingSource: string[] = Array.isArray(status?.outstandingSteps)
      ? (status?.outstandingSteps as string[])
      : [];
    const outstanding = outstandingSource.filter(
      step => typeof step === 'string' && step.trim().length > 0
    );
    const trimmedNote =
      typeof status?.note === 'string' && status.note.trim().length > 0
        ? status.note.trim()
        : null;
    const serviceReport = status?.serviceReport?.document ? status.serviceReport : null;
    const summary = typeof status?.summary === 'string' ? status.summary.trim() : '';
    return {
      status,
      state,
      badgeLabel,
      badgeColors,
      outstanding,
      note: trimmedNote,
      summary,
      formattedSummary: formatHandoffSummaryText(summary),
      serviceReport,
      isReady: state === 'COMPLETE' || state === 'NOT_REQUIRED',
      isNotRequired: state === 'NOT_REQUIRED' || status?.notRequired === true,
      readyDescription:
        config.preInstallCheck.readyDescription ||
        'Pre-install is complete and ready for installation execution.',
      notRequiredDescription:
        config.preInstallCheck.notRequiredDescription ||
        'Pre-install is marked not required for this installation.',
      completeActionLabel: config.preInstallCheck.completeActionLabel || 'Complete pre-install',
      openActionLabel: config.preInstallCheck.openActionLabel || 'Open pre-install workspace',
      requireActionLabel: config.preInstallCheck.requireActionLabel || 'Require pre-install'
    };
  }, [config.preInstallCheck, phaseMeta]);

  const handleViewPreInstallServiceReport = useCallback(() => {
    const document = preInstallCardData?.serviceReport?.document || null;
    if (!document) return;
    setServiceReportDraft(document);
    setServiceReportError(null);
    setServiceReportMode('view');
    setServiceReportModalOpen(true);
  }, [preInstallCardData?.serviceReport?.document]);

  const renderReferenceImageTextLinks = useCallback(
    (step: PrepStep, item: ChecklistItem) => {
      const itemText = composeChecklistItemText(item);
      const labelText = normalizeWhitespace(item.label || '');
      const isMeasurementItem = item.capture?.type === 'MEASUREMENT';
      const specText = isMeasurementItem ? '' : normalizeWhitespace(item.spec || '');
      const fallbackContent = (
        <>
          <span style={styles.checklistPrimaryText}>{labelText}</span>
          {specText && <span style={styles.checklistDetailText}>: {specText}</span>}
        </>
      );
      if (!itemText) {
        return { content: fallbackContent, hasLinks: false };
      }

      const stepReferenceImages = dedupeReferenceImages(
        Array.isArray(step.metadata.referenceImages) ? step.metadata.referenceImages : []
      );
      const stepReferenceBlocks = Array.isArray(step.metadata.referenceImageBlocks)
        ? step.metadata.referenceImageBlocks
        : [];
      const block = stepReferenceBlocks.find(candidate => {
        const candidateItemId = String((candidate as { itemId?: string })?.itemId || '').trim();
        if (candidateItemId && item.id && candidateItemId === String(item.id)) {
          return true;
        }
        const candidateText = normalizeWhitespace(String(candidate?.text || ''));
        return candidateText && candidateText === itemText;
      });

      if (!block || !Array.isArray(block.imageIds) || block.imageIds.length === 0) {
        return { content: fallbackContent, hasLinks: false };
      }

      const blockImageIds = block.imageIds.map(String);
      const imageById = new Map(stepReferenceImages.map(image => [String(image.id), image]));
      const blockImages = dedupeReferenceImages(
        blockImageIds
          .map(imageId => imageById.get(imageId))
          .filter((image): image is ReferenceImageHit => Boolean(image?.id))
      );
      if (blockImages.length === 0) {
        return { content: fallbackContent, hasLinks: false };
      }

      const anchors = Array.isArray(block.anchors) ? block.anchors : [];
      const linkText = (text: string, textKey: string) => {
        const occupiedRanges: Array<{ start: number; end: number }> = [];
        const renderableAnchors = anchors
          .map((anchor, anchorIndex) => {
            const anchorText = String(anchor?.text || '').trim();
            if (!anchorText) return null;
            const anchorImageIds =
              Array.isArray(anchor?.imageIds) && anchor.imageIds.length > 0
                ? anchor.imageIds.map(String)
                : blockImageIds;
            const gallery = dedupeReferenceImages(
              anchorImageIds
                .map(imageId => imageById.get(imageId))
                .filter((image): image is ReferenceImageHit => Boolean(image?.id))
            );
            if (gallery.length === 0) return null;
            const range = findNonOverlappingPhraseRange(text, anchorText, occupiedRanges);
            if (!range) return null;
            occupiedRanges.push(range);
            return {
              key: `${textKey}:${anchorText}:${anchorIndex}:${range.start}`,
              start: range.start,
              end: range.end,
              gallery
            };
          })
          .filter(
            (
              anchor
            ): anchor is {
              key: string;
              start: number;
              end: number;
              gallery: ReferenceImageHit[];
            } => Boolean(anchor)
          )
          .sort((left, right) => left.start - right.start);

        if (renderableAnchors.length === 0) return { content: text, hasLinks: false };

        const parts: React.ReactNode[] = [];
        let cursor = 0;
        renderableAnchors.forEach(anchor => {
          if (anchor.start > cursor) {
            parts.push(text.slice(cursor, anchor.start));
          }
          parts.push(
            <button
              type="button"
              key={`install-image-anchor:${step.id}:${item.id}:${anchor.key}`}
              style={styles.inlineImageTextLink}
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                openPreviewImages(anchor.gallery);
              }}
            >
              {text.slice(anchor.start, anchor.end)}
            </button>
          );
          cursor = anchor.end;
        });
        if (cursor < text.length) {
          parts.push(text.slice(cursor));
        }
        return { content: parts, hasLinks: true };
      };

      const linkedLabel = linkText(labelText, 'label');
      const linkedSpec = specText ? linkText(specText, 'spec') : { content: null, hasLinks: false };

      return {
        content: (
          <>
            <span style={styles.checklistPrimaryText}>{linkedLabel.content}</span>
            {specText && <span style={styles.checklistDetailText}>: {linkedSpec.content}</span>}
          </>
        ),
        hasLinks: linkedLabel.hasLinks || linkedSpec.hasLinks
      };
    },
    [openPreviewImages]
  );

  const renderAttachmentList = useCallback(
    (step: PrepStep, emptyLabel: string) => {
      const attachments = (Array.isArray(step.metadata.attachments)
        ? step.metadata.attachments
        : []
      ).filter((attachment): attachment is StepAttachment => Boolean(attachment?.id));
      if (attachments.length === 0) {
        return <div style={styles.attachmentPlaceholder}>{emptyLabel}</div>;
      }
      const imageGallery = buildAttachmentPreviewGallery(attachments);

      return (
        <div style={styles.attachmentGrid}>
          {attachments.map(attachment => {
            const attachmentUrl = getAttachmentDisplayUrl(attachment);
            const imageAttachment = isImageAttachment(attachment) && Boolean(attachmentUrl);
            const sizeLabel = formatAttachmentSize(attachment.size);
            const typeLabel = imageAttachment
              ? 'Image'
              : (attachment.mimeType || 'File').split('/').pop()?.toUpperCase() || 'File';
            const removeInProgress = attachmentRemovingId === attachment.id;
            const openAttachment = () => {
              if (imageAttachment) {
                openPreviewImages(imageGallery, attachment.id);
                return;
              }
              void openStoredAttachment(attachment);
            };

            return (
              <div key={attachment.id} style={styles.attachmentCard}>
                <button
                  type="button"
                  style={styles.attachmentThumbnailButton}
                  onClick={openAttachment}
                  disabled={!attachmentUrl}
                  aria-label={`Open ${attachment.name || 'attachment'}`}
                >
                  {imageAttachment ? (
                    <AuthenticatedReferenceImage
                      src={attachmentUrl}
                      alt={attachment.name || 'Task attachment'}
                      style={styles.attachmentThumb}
                      placeholderStyle={styles.attachmentThumbPlaceholder}
                      placeholderText="Preview unavailable"
                    />
                  ) : (
                    <div style={styles.attachmentThumbPlaceholder}>{typeLabel}</div>
                  )}
                </button>
                <div style={styles.attachmentMeta}>
                  <button
                    type="button"
                    style={styles.attachmentNameButton}
                    onClick={openAttachment}
                    disabled={!attachmentUrl}
                  >
                    {attachment.name || 'Attachment'}
                  </button>
                  <div style={styles.attachmentSubtext}>
                    {[typeLabel, sizeLabel].filter(Boolean).join(' | ')}
                  </div>
                </div>
                {!workspaceReadOnly && (
                  <button
                    type="button"
                    style={{
                      ...styles.removeAttachment,
                      ...(removeInProgress ? styles.removeAttachmentDisabled : {})
                    }}
                    onClick={() => handleAttachmentRemove(step.id, attachment.id)}
                    disabled={removeInProgress}
                  >
                    {removeInProgress ? 'Removing...' : 'Remove'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      );
    },
    [
      attachmentRemovingId,
      handleAttachmentRemove,
      openPreviewImages,
      openStoredAttachment,
      workspaceReadOnly
    ]
  );

  const renderMeasurementCapture = useCallback(
    (stepId: string, item: ChecklistItem) => {
      if (!MEASUREMENT_CAPTURE_ENABLED || item.capture?.type !== 'MEASUREMENT') return null;
      const capture = item.capture;
      const mode = getCaptureUiMode(capture);
      if (mode !== 'numeric') return null;

      const captureValue = String(item.captureValue || '');
      const placeholder = formatMeasurementPlaceholder(capture);
      const hint = formatMeasurementHint(capture);
      const itemSpec = normalizeWhitespace(item.spec || '');
      const details = [itemSpec, hint].filter(
        (detail, index, list): detail is string =>
          Boolean(detail) && list.findIndex(candidate => candidate === detail) === index
      );
      const detailsKey = `${stepId}:${item.id}`;
      const detailsOpen = captureDetailsOpen[detailsKey] === true;

      let inputStyle: React.CSSProperties = { ...styles.captureInput };
      if (workspaceReadOnly) {
        inputStyle = { ...inputStyle, background: BRAND_SURFACE, cursor: 'not-allowed' };
      }

      return (
        <div style={styles.captureRow}>
          <div style={styles.captureInputGroup}>
            <div style={styles.captureLabelRow}>
              <label style={styles.captureLabel}>Measured value</label>
              {details.length > 0 && (
                <button
                  type="button"
                  style={styles.captureDetailsToggle}
                  onClick={() => toggleCaptureDetails(stepId, item.id)}
                >
                  {detailsOpen ? 'Hide details' : 'Show details'}
                </button>
              )}
            </div>
            <input
              style={inputStyle}
              type="text"
              value={captureValue}
              placeholder={placeholder}
              onChange={event =>
                handleChecklistCaptureValueChange(stepId, item.id, event.target.value)
              }
              disabled={workspaceReadOnly}
            />
          </div>
          {detailsOpen && details.length > 0 && (
            <div style={styles.captureHint}>{details.join(' | ')}</div>
          )}
        </div>
      );
    },
    [captureDetailsOpen, handleChecklistCaptureValueChange, toggleCaptureDetails, workspaceReadOnly]
  );

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          Back to cases
        </button>
        <div style={styles.headerActions}>
          {lastSavedAt && (
            <span style={styles.savedAt}>Last saved {new Date(lastSavedAt).toLocaleString()}</span>
          )}
          <button
            type="button"
            style={{ ...styles.button, ...styles.primaryButton }}
            onClick={() => handleSave()}
            disabled={saving || workspaceReadOnly}
          >
            {saving ? 'Saving...' : 'Save checklist'}
          </button>
        </div>
      </div>

      {workspaceReadOnly && (
        <div style={styles.readOnlyBanner}>
          Read-only view. Only the assigned technician can edit this task
          {assignedLabel ? ` (assigned to ${assignedLabel}).` : '.'}
        </div>
      )}

      {caseDisplay && (
        <div style={styles.caseHeaderCard}>
          <div style={styles.caseHeaderTop}>
            <div style={styles.caseHeaderCopy}>
              <div style={styles.caseHeroTitle}>{config.copy.caseLabel}</div>
              <div style={styles.caseHeroProblem}>{caseDisplay.problemDescription}</div>
            </div>
            <div style={styles.caseHeroActions}>
              <button
                type="button"
                style={{
                  ...styles.button,
                  ...styles.primaryButton,
                  opacity: refreshing || loading || workspaceReadOnly ? 0.75 : 1
                }}
                onClick={handleRegenerateChecklist}
                disabled={refreshing || loading || workspaceReadOnly}
              >
                {refreshing ? 'Reloading...' : 'Reload reference'}
              </button>
            </div>
          </div>
          <div style={styles.caseHeaderGrid}>
            <div style={styles.caseHeaderField}>
              <div style={styles.caseHeaderFieldLabel}>Case</div>
              <div style={styles.caseHeaderFieldValue}>{caseDisplay.id}</div>
            </div>
            <div style={styles.caseHeaderField}>
              <div style={styles.caseHeaderFieldLabel}>Customer</div>
              <div style={styles.caseHeaderFieldValue}>{caseDisplay.customer}</div>
            </div>
            <div style={styles.caseHeaderField}>
              <div style={styles.caseHeaderFieldLabel}>Device</div>
              <div style={styles.caseHeaderFieldValue}>{caseDisplay.device}</div>
            </div>
            <div style={styles.caseHeaderField}>
              <div style={styles.caseHeaderFieldLabel}>Reference</div>
              <div style={styles.caseHeaderFieldValue}>{referenceLabel}</div>
            </div>
            <div style={styles.caseHeaderField}>
              <div style={styles.caseHeaderFieldLabel}>Contact</div>
              <div style={styles.caseHeaderFieldValue}>{caseDisplay.contact}</div>
            </div>
          </div>
        </div>
      )}

      {config.preInstallCheck && preInstallCardData && (
        <div
          style={{
            ...styles.preInstallCard,
            ...(preInstallCardData.isReady ? styles.preInstallCardReady : {})
          }}
        >
          <div style={styles.preInstallCompactRow}>
            <div style={styles.preInstallCompactMain}>
              <div style={styles.preInstallTitleRow}>
                <div style={styles.preInstallTitle}>{config.preInstallCheck.title}</div>
                <span
                  style={{
                    ...styles.preInstallBadge,
                    backgroundColor: preInstallCardData.badgeColors.bg,
                    color: preInstallCardData.badgeColors.color
                  }}
                >
                  {preInstallCardData.badgeLabel}
                </span>
              </div>
              <div style={styles.preInstallSubtitle}>
                {preInstallCardData.isReady
                  ? preInstallCardData.isNotRequired
                    ? preInstallCardData.notRequiredDescription
                    : preInstallCardData.readyDescription
                  : config.preInstallCheck.description || 'Pre-install readiness has not been cleared yet.'}
              </div>
            </div>
            <div style={styles.preInstallCompactActions}>
              {preInstallCardData.serviceReport && (
                <button
                  type="button"
                  style={styles.preInstallActionButton}
                  onClick={handleViewPreInstallServiceReport}
                >
                  Open report
                </button>
              )}
              {!preInstallCardData.isReady && config.preInstallCheck.actionLabel && (
                <button
                  type="button"
                  style={{
                    ...styles.preInstallActionButton,
                    opacity: caseId ? 1 : 0.6,
                    cursor: caseId ? 'pointer' : 'not-allowed'
                  }}
                  onClick={handleOpenPhaseHandoff}
                  disabled={!caseId}
                >
                  {preInstallCardData.completeActionLabel}
                </button>
              )}
              {!workspaceReadOnly && !preInstallCardData.isNotRequired && (
                <button
                  type="button"
                  style={styles.preInstallGhostButton}
                  onClick={handleMarkPreInstallNotRequired}
                  disabled={saving}
                >
                  Mark not required
                </button>
              )}
              {!workspaceReadOnly && preInstallCardData.isNotRequired && (
                <button
                  type="button"
                  style={styles.preInstallGhostButton}
                  onClick={handleRequirePreInstall}
                  disabled={saving}
                >
                  {preInstallCardData.requireActionLabel}
                </button>
              )}
              {(preInstallCardData.summary || preInstallCardData.outstanding.length > 0 || preInstallCardData.note) && (
                <button
                  type="button"
                  style={styles.preInstallDetailsButton}
                  onClick={() => setPreInstallDetailsOpen(open => !open)}
                >
                  {preInstallDetailsOpen ? 'Hide details' : 'Show details'}
                </button>
              )}
            </div>
          </div>
          {preInstallDetailsOpen && (
            <div style={styles.preInstallDetails}>
              {preInstallCardData.formattedSummary && (
                <div style={styles.preInstallSummary}>{preInstallCardData.formattedSummary}</div>
              )}
              {preInstallCardData.outstanding.length > 0 && (
                <div>
                  <div style={styles.preInstallListTitle}>
                    {config.preInstallCheck.outstandingTitle || 'Outstanding items'}
                  </div>
                  <ul style={styles.preInstallList}>
                    {preInstallCardData.outstanding.map(item => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
              {preInstallCardData.note && (
                <div style={styles.preInstallNote}>
                  <div style={styles.preInstallNoteLabel}>
                    {config.preInstallCheck.noteLabel || 'Technician note'}
                  </div>
                  <div>{preInstallCardData.note}</div>
                </div>
              )}
              {preInstallCardData.isReady && config.preInstallCheck.actionLabel && (
                <div style={styles.preInstallActions}>
                  <button
                    type="button"
                    style={{
                      ...styles.preInstallActionButton,
                      opacity: caseId ? 1 : 0.6,
                      cursor: caseId ? 'pointer' : 'not-allowed'
                    }}
                    onClick={handleOpenPhaseHandoff}
                    disabled={!caseId}
                  >
                    {preInstallCardData.openActionLabel}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <fieldset disabled={workspaceReadOnly} style={styles.readOnlyFieldset}>
      <div style={styles.statsCard}>
        <div style={styles.statusHeader}>
          <div style={styles.statusLeft}>
            <div>
              <div style={styles.statLabel}>Status</div>
              <div style={styles.statusChipRow}>
                <span style={statusChipStyle}>{headerStatusLabel}</span>
              </div>
            </div>
            <div style={styles.statusMetaRow}>
              <div style={{ ...styles.statusMetaItem, minWidth: '9rem' }}>
                <div style={styles.statLabel}>Checklist progress</div>
                <div style={styles.statusMetaValue}>
                  {completionProgress.completed} of {completionProgress.total}
                </div>
              </div>
              <div style={{ ...styles.statusMetaItem, flex: '1 1 22rem', minWidth: '18rem' }}>
                <div style={styles.statLabel}>Reference</div>
                <div style={styles.statusMetaValue}>{referenceLabel}</div>
                <div style={styles.statusMetaSubnote}>Checklist source currently loaded for this task.</div>
              </div>
              <div style={{ ...styles.statusMetaItem, minWidth: '12rem', maxWidth: '14rem' }}>
                <div style={styles.statLabel}>{config.copy.totalTimeLabel}</div>
                <input
                  type="text"
                  style={styles.timeInput}
                  placeholder="e.g. 1.5"
                  value={totalTimeHours}
                  onChange={event => {
                    setTotalTimeHours(event.target.value);
                    setHasUnsavedChanges(true);
                  }}
                />
              </div>
            </div>
          </div>
          <div style={styles.statusRight}>
            <div style={styles.statLabel}>Actions</div>
            <div style={styles.primaryActions}>
              <button
                type="button"
                style={{
                  ...styles.statusActionComplete,
                  ...((taskStatus === 'COMPLETED' || saving || workspaceReadOnly)
                    ? styles.statusActionDisabled
                    : {})
                }}
                onClick={() => handleSave({ status: 'COMPLETED' })}
                disabled={taskStatus === 'COMPLETED' || saving || workspaceReadOnly}
              >
                {config.copy.markCompleteButtonLabel}
              </button>
              <button
                type="button"
                style={{
                  ...styles.statusActionHold,
                  ...((taskStatus === 'ON_HOLD' || taskStatus === 'COMPLETED' || saving || workspaceReadOnly)
                    ? styles.statusActionDisabled
                    : {})
                }}
                onClick={() => handleSave({ status: 'ON_HOLD' })}
                disabled={taskStatus === 'ON_HOLD' || taskStatus === 'COMPLETED' || saving || workspaceReadOnly}
              >
                Place on hold
              </button>
              <button
                type="button"
                style={{
                  ...styles.statusActionReopen,
                  ...((taskStatus === 'IN_PROGRESS' || saving || workspaceReadOnly)
                    ? styles.statusActionDisabled
                    : {})
                }}
                onClick={() => handleSave({ status: 'IN_PROGRESS' })}
                disabled={taskStatus === 'IN_PROGRESS' || saving || workspaceReadOnly}
              >
                Re-open
              </button>
            </div>
            <button
              type="button"
              style={{
                  ...styles.reportActionButton,
                  ...((reportLoading || saving || workspaceReadOnly || taskStatus !== 'COMPLETED') ? styles.reportActionButtonDisabled : {})
                }}
                onClick={handleGenerateReport}
                disabled={reportLoading || saving || workspaceReadOnly || taskStatus !== 'COMPLETED'}
            >
              {reportLoading
                ? config.copy.summaryGeneratingLabel
                : reportSummary
                ? 'Regenerate summary'
                : config.copy.summaryGenerateLabel}
            </button>
            {reportError && <div style={styles.reportInfoError}>{reportError}</div>}
            {reportSummary && reportGeneratedAt && (
              <div style={styles.reportInfo}>
                {reportIsCustom ? 'Last updated' : 'Last generated'} {new Date(reportGeneratedAt).toLocaleString()}
              </div>
            )}
          </div>
        </div>
        <div style={styles.runNotes}>
          <label style={styles.statLabel}>{config.copy.runNotesLabel}</label>
          <textarea
            style={styles.textarea}
            rows={3}
            value={runNotes}
            onChange={event => {
              setRunNotes(event.target.value);
              setHasUnsavedChanges(true);
            }}
          />
        </div>
      </div>

      {reportSummary || reportEditing ? (
        <div style={styles.reportCard}>
          <div style={styles.reportHeader}>
            <div>
              <div style={styles.reportTitle}>Customer summary</div>
              <div style={styles.reportMeta}>
                {reportSummary
                  ? `Last updated ${
                      reportGeneratedAt ? new Date(reportGeneratedAt).toLocaleString() : ''
                    }${reportIsCustom ? ' - Edited manually' : ''}`
                  : config.copy.summaryNoDataLabel}
              </div>
            </div>
            <div style={styles.reportActions}>
              {reportSummary && !reportEditing && workspace?.serviceReport?.document && (
                <button
                  type="button"
                  style={styles.reportSecondaryButton}
                  onClick={handleViewServiceReport}
                  disabled={serviceReportLoading || serviceReportSaving || serviceReportSending}
                >
                  View service report
                </button>
              )}
              {reportSummary && !reportEditing && canRebuildServiceReport && (
                <button
                  type="button"
                  style={styles.reportSecondaryButton}
                  onClick={handleCreateServiceReport}
                  disabled={serviceReportLoading || serviceReportSaving || serviceReportSending || reportLoading || taskStatus !== 'COMPLETED'}
                >
                  {serviceReportLoading
                    ? 'Building service report...'
                    : workspace?.serviceReport?.document
                    ? 'Regenerate service report'
                    : 'Create service report'}
                </button>
              )}
              {reportSummary && !reportEditing && (
                <button
                  type="button"
                  style={styles.reportSecondaryButton}
                  onClick={handleStartReportEdit}
                  disabled={reportLoading || workspaceReadOnly || taskStatus !== 'COMPLETED'}
                >
                  {config.copy.summaryEditLabel}
                </button>
              )}
              {reportEditing && (
                <button
                  type="button"
                  style={styles.reportSecondaryButton}
                  onClick={handleCancelReportEdit}
                  disabled={reportLoading}
                >
                  Cancel edit
                </button>
              )}
            </div>
          </div>
          {reportEditing ? (
            <>
              <textarea
                style={{ ...styles.reportTextarea, minHeight: 360 }}
                rows={16}
                value={reportDraft}
                onChange={event => setReportDraft(event.target.value)}
              />
              {reportEditError && <div style={styles.reportInfoError}>{reportEditError}</div>}
              <div style={styles.reportEditActions}>
                <button
                  type="button"
                  style={styles.reportPrimaryButton}
                  onClick={handleSaveReportEdit}
                  disabled={reportLoading || workspaceReadOnly || taskStatus !== 'COMPLETED'}
                >
                  Save summary
                </button>
                <button
                  type="button"
                  style={styles.reportSecondaryButton}
                  onClick={handleCancelReportEdit}
                  disabled={reportLoading}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <div style={styles.reportBody}>{reportSummary}</div>
          )}
        </div>
      ) : null}
      </fieldset>

      <div style={styles.expertSection}>
        <div style={styles.expertHeader}>
          <div style={styles.expertHeaderCopy}>
            <div style={styles.expertTitle}>Device Expert</div>
            <div style={styles.expertSub}>
              Ask about a selected checklist step for contextual help, or ask an open device
              question.
            </div>
          </div>
          <button
            type="button"
            style={{ ...styles.button, ...styles.secondaryButton }}
            onClick={() => setDeviceExpertOpen(prev => !prev)}
          >
            {deviceExpertOpen ? 'Hide' : 'Show'}
          </button>
        </div>
        {deviceExpertOpen && (
          <>
            <div style={styles.expertGrid}>
              <div style={styles.expertField}>
                <label style={styles.expertLabel}>Checklist step</label>
                <select
                  style={styles.expertSelect}
                  value={deviceExpertStepId}
                  onChange={event => {
                    setDeviceExpertStepId(event.target.value);
                    setDeviceExpertContextKey('');
                    setDeviceExpertError(null);
                  }}
                  disabled={deviceExpertLoading || deviceExpertParentOptions.length === 0}
                >
                  <option value="">No specific checklist step selected</option>
                  {deviceExpertParentOptions.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.sequence}. {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.expertField}>
                <label style={styles.expertLabel}>Checklist item</label>
                <select
                  style={styles.expertSelect}
                  value={deviceExpertContextKey}
                  onChange={event => {
                    setDeviceExpertContextKey(event.target.value);
                    setDeviceExpertError(null);
                  }}
                  disabled={deviceExpertLoading || !selectedDeviceExpertParent}
                >
                  <option value="">
                    {selectedDeviceExpertParent
                      ? 'No checklist item selected'
                      : 'Select a checklist step first'}
                  </option>
                  {deviceExpertContextOptions.map(option => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div style={styles.expertField}>
              <label style={styles.expertLabel}>Question</label>
              <input
                style={styles.expertInput}
                value={deviceExpertQuestion}
                onChange={event => {
                  setDeviceExpertQuestion(event.target.value);
                  setDeviceExpertError(null);
                }}
                placeholder="Ask an installation question, or leave blank to get more detail on the selected checklist item."
                disabled={deviceExpertLoading || !deviceExpertModelId}
              />
              <div style={styles.expertHint}>
                {deviceExpertModelId
                  ? 'Choosing a checklist item adds that task context. With no step selected, your question is treated as an open Device Expert ask.'
                  : 'This installation task has no device-model context yet, so Device Expert is unavailable.'}
              </div>
            </div>
            <div style={styles.expertActions}>
              <button
                type="button"
                style={{
                  ...styles.button,
                  ...styles.primaryButton,
                  opacity:
                    deviceExpertLoading ||
                    !deviceExpertModelId ||
                    (!deviceExpertQuestion.trim() && !selectedDeviceExpertContext)
                      ? 0.65
                      : 1,
                  cursor:
                    deviceExpertLoading ||
                    !deviceExpertModelId ||
                    (!deviceExpertQuestion.trim() && !selectedDeviceExpertContext)
                      ? 'not-allowed'
                      : 'pointer'
                }}
                onClick={askDeviceExpert}
                disabled={
                  deviceExpertLoading ||
                  !deviceExpertModelId ||
                  (!deviceExpertQuestion.trim() && !selectedDeviceExpertContext)
                }
              >
                {deviceExpertLoading ? 'Asking...' : 'Ask Device Expert'}
              </button>
              <button
                type="button"
                style={{
                  ...styles.button,
                  ...styles.secondaryButton,
                  opacity: deviceExpertLoading ? 0.65 : 1,
                  cursor: deviceExpertLoading ? 'not-allowed' : 'pointer'
                }}
                onClick={() => {
                  setDeviceExpertQuestion('');
                  setDeviceExpertMessages([]);
                  setDeviceExpertError(null);
                  setDeviceExpertConversationId(null);
                }}
                disabled={deviceExpertLoading}
              >
                Clear
              </button>
            </div>
            {deviceExpertError && <div style={styles.expertError}>{deviceExpertError}</div>}
            {deviceExpertMessages.length > 0 && (
              <div style={styles.expertMessages}>
                {deviceExpertMessages.map(message => (
                  <div
                    key={message.id}
                    style={{
                      ...styles.expertMessage,
                      ...(message.role === 'assistant'
                        ? styles.expertAssistant
                        : styles.expertUser)
                    }}
                  >
                    <strong>{message.role === 'assistant' ? 'Assistant' : 'You'}:</strong>{' '}
                    {message.role === 'assistant'
                      ? formatDeviceExpertText(message.content)
                      : message.content}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <fieldset disabled={workspaceReadOnly} style={styles.readOnlyFieldset}>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {loading ? (
        <div style={styles.loadingState}>{config.copy.loadingMessage}</div>
      ) : (
        <>
          {steps.map(step => {
            const layoutType =
              config.enforceLayout ??
              (typeof step.metadata.layout === 'string' && step.metadata.layout.length > 0
                ? step.metadata.layout
                : step.stepKey === 'SITE_CHECK'
                  ? 'site'
                  : 'standard');
            const isCompactLayout = layoutType === 'site' || layoutType === 'compact';
            const attachmentLabel =
              step.metadata.attachmentLabel ||
              (layoutType === 'site' ? 'Site photos / documents' : 'Attachments');
            const attachmentHint =
              step.metadata.attachmentHint ||
              (layoutType === 'site'
                ? 'Upload customer forms, site pictures, or acceptance sheets.'
                : 'Upload supporting files, photos, or paperwork.');
            const attachmentButtonLabel = layoutType === 'site' ? 'Add photo / file' : 'Add files';
            const attachmentEmptyLabel =
              layoutType === 'site' ? 'No supporting files attached yet.' : 'No attachments yet.';
            const allowUploads = step.metadata.allowUploads !== false;
            const allowNotes =
              config.disableStepNotes === true ? false : step.metadata.allowNotes !== false;
            const isNotApplicable = step.metadata.notApplicable === true;
            const selectedNotApplicableOption = NOT_APPLICABLE_OPTIONS.find(
              option => option.value === (step.metadata.notApplicableReason || '')
            );

            const notApplicableControls = (
              <div
                style={layoutType === 'site' ? styles.notApplicableBoxSite : styles.notApplicableBox}
              >
                <div style={styles.notApplicableHeader}>
                  <div style={styles.notApplicableTitle}>Procedure applicability</div>
                  <label style={styles.inlineToggle}>
                    <input
                      type="checkbox"
                      checked={isNotApplicable}
                      onChange={event => handleNotApplicableToggle(step.id, event.target.checked)}
                    />
                    Not applicable
                  </label>
                </div>
                {isNotApplicable && (
                  <>
                    <select
                      style={styles.notApplicableSelect}
                      value={step.metadata.notApplicableReason || ''}
                      onChange={event => handleNotApplicableReasonChange(step.id, event.target.value)}
                    >
                      <option value="" disabled>
                        Select a reason
                      </option>
                      {NOT_APPLICABLE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <textarea
                      style={styles.notApplicableNotes}
                      rows={3}
                      placeholder="Explain why this step is not required..."
                      value={step.metadata.notApplicableNote || ''}
                      onChange={event => handleNotApplicableNoteChange(step.id, event.target.value)}
                    />
                    {selectedNotApplicableOption?.helper && (
                      <div style={styles.notApplicableHint}>{selectedNotApplicableOption.helper}</div>
                    )}
                  </>
                )}
              </div>
            );

            return (
              <div key={step.id} style={styles.stepWrapper}>
                {isCompactLayout ? (
                  <div
                    style={{
                      ...styles.siteCard,
                      ...(step.metadata.completionState === 'COMPLETE' ? styles.siteCardComplete : {}),
                      ...(step.metadata.completionState === 'PARTIAL' ? styles.siteCardPartial : {})
                    }}
                  >
                    <div style={styles.siteHeader}>
                      <div>
                        <div style={styles.siteTitle}>
                          {step.sequence}. {step.label}
                        </div>
                        <div style={styles.siteMetaRow}>
                          <span style={styles.siteMeta}>{step.status || 'Pending'}</span>
                          {isNotApplicable && (
                            <span style={styles.notApplicableBadge}>Marked not applicable</span>
                          )}
                        </div>
                      </div>
                      <div style={styles.siteHeaderActions}>
                        {step.metadata.allowSkip && (
                          <label style={styles.inlineToggle}>
                            <input
                              type="checkbox"
                              checked={step.metadata.skipped === true}
                              onChange={event => handleSkipSoftware(step.id, event.target.checked)}
                            />
                            Skip
                          </label>
                        )}
                        {typeof step.metadata.repackAcknowledged === 'boolean' && (
                          <label style={styles.inlineToggle}>
                            <input
                              type="checkbox"
                              checked={step.metadata.repackAcknowledged}
                              onChange={event => handleRepackAck(step.id, event.target.checked)}
                            />
                            Repack acknowledged
                          </label>
                        )}
                        <button
                          type="button"
                          style={styles.flagButton}
                          onClick={() => handleOpenFeedbackModal(step)}
                        >
                          Flag documentation issue
                        </button>
                        <button
                          type="button"
                          style={styles.collapseButton}
                          onClick={() =>
                            setCollapsedSteps(prev => ({
                              ...prev,
                              [step.id]: !prev[step.id]
                            }))
                          }
                        >
                          {collapsedSteps[step.id] ? 'Show' : 'Hide'}
                        </button>
                      </div>
                    </div>
                    {!collapsedSteps[step.id] && (
                      <>
                        {Array.isArray(step.metadata.checklist) &&
                          step.metadata.checklist.length > 0 && (
                            <div style={styles.siteChecklist}>
                              {step.metadata.checklist.map(item => {
                                const linkedText = renderReferenceImageTextLinks(step, item);
                                return (
                                  <div key={item.id} style={styles.siteChecklistItem}>
                                    <label style={styles.siteChecklistToggle}>
                                      <input
                                        type="checkbox"
                                        checked={item.completed}
                                        onChange={() => toggleChecklistItem(step.id, item.id)}
                                      />
                                      <div style={styles.siteChecklistText}>
                                        <span style={styles.siteChecklistLabel}>
                                          {linkedText.content}
                                        </span>
                                      </div>
                                    </label>
                                    {renderMeasurementCapture(step.id, item)}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        {allowUploads && (
                          <div style={styles.siteAttachments}>
                            <div style={styles.siteAttachmentsHeader}>
                              <div>
                                <div style={styles.fieldLabel}>{attachmentLabel}</div>
                                {attachmentHint && (
                                  <div style={styles.siteAttachmentHint}>{attachmentHint}</div>
                                )}
                              </div>
                              {!workspaceReadOnly && (
                                <label
                                  style={{
                                    ...styles.addPhotoButton,
                                    ...(attachmentUploadingStepId === step.id
                                      ? styles.uploadLabelDisabled
                                      : {})
                                  }}
                                >
                                  {attachmentUploadingStepId === step.id
                                    ? 'Uploading...'
                                    : attachmentButtonLabel}
                                  <input
                                    type="file"
                                    style={{ display: 'none' }}
                                    multiple
                                    accept="image/*,application/pdf"
                                    disabled={attachmentUploadingStepId === step.id}
                                    onChange={event => handleAttachmentAdd(step.id, event)}
                                  />
                                </label>
                              )}
                            </div>
                            {renderAttachmentList(step, attachmentEmptyLabel)}
                          </div>
                        )}
                        {notApplicableControls}
                        {allowNotes && (
                          <div>
                            <label style={styles.fieldLabel}>Notes / observations</label>
                            <textarea
                              style={styles.textarea}
                              rows={3}
                              value={step.notes || ''}
                              onChange={event => handleStepNotesChange(step.id, event.target.value)}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                <div
                  style={{
                    ...styles.stepCard,
                    ...(step.metadata.completionState === 'COMPLETE' ? styles.stepCardComplete : {}),
                    ...(step.metadata.completionState === 'PARTIAL' ? styles.stepCardPartial : {})
                  }}
                >
                  <div style={styles.stepHeader}>
                    <div>
                      <div style={styles.stepLabel}>
                        {step.sequence}. {step.label}
                      </div>
                        <div style={styles.stepMetaRow}>
                          <span style={styles.stepMeta}>{step.status || 'PENDING'}</span>
                          {isNotApplicable && (
                            <span style={styles.notApplicableBadge}>Marked not applicable</span>
                          )}
                        </div>
                      </div>
                      <div style={styles.stepHeaderActions}>
                        {step.metadata.allowSkip && (
                          <label style={styles.inlineToggle}>
                            <input
                              type="checkbox"
                              checked={step.metadata.skipped === true}
                              onChange={event => handleSkipSoftware(step.id, event.target.checked)}
                            />
                            Skip (not required)
                          </label>
                        )}
                        {typeof step.metadata.repackAcknowledged === 'boolean' && (
                          <label style={styles.inlineToggle}>
                            <input
                              type="checkbox"
                              checked={step.metadata.repackAcknowledged}
                              onChange={event => handleRepackAck(step.id, event.target.checked)}
                            />
                            Repack acknowledged
                          </label>
                        )}
                        <button
                          type="button"
                          style={styles.flagButton}
                          onClick={() => handleOpenFeedbackModal(step)}
                        >
                          Flag documentation issue
                        </button>
                      </div>
                    </div>

                    {Array.isArray(step.metadata.checklist) &&
                      step.metadata.checklist.length > 0 && (
                        <div style={styles.checklist}>
                          {step.metadata.checklist.map(item => {
                            const linkedText = renderReferenceImageTextLinks(step, item);
                            return (
                              <div key={item.id} style={styles.checklistItem}>
                                <div style={styles.checklistContent}>
                                  <label style={styles.checklistLabel}>
                                    <input
                                      type="checkbox"
                                      checked={item.completed}
                                      onChange={() => toggleChecklistItem(step.id, item.id)}
                                    />
                                    <span>{linkedText.content}</span>
                                  </label>
                                  {renderMeasurementCapture(step.id, item)}
                                  <textarea
                                    style={styles.checklistNotes}
                                    placeholder="Notes / observations"
                                    value={item.notes || ''}
                                    onChange={event =>
                                      handleChecklistNoteChange(
                                        step.id,
                                        item.id,
                                        event.target.value
                                      )
                                    }
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                    {allowUploads && (
                      <div style={styles.attachments}>
                        <div style={styles.attachmentsHeader}>
                          <span>{attachmentLabel}</span>
                          {!workspaceReadOnly && (
                            <label
                              style={{
                                ...styles.uploadLabel,
                                ...(attachmentUploadingStepId === step.id
                                  ? styles.uploadLabelDisabled
                                  : {})
                              }}
                            >
                              {attachmentUploadingStepId === step.id
                                ? 'Uploading...'
                                : attachmentButtonLabel}
                              <input
                                type="file"
                                style={{ display: 'none' }}
                                multiple
                                accept="image/*,application/pdf"
                                disabled={attachmentUploadingStepId === step.id}
                                onChange={event => handleAttachmentAdd(step.id, event)}
                              />
                            </label>
                          )}
                        </div>
                        {renderAttachmentList(step, attachmentEmptyLabel)}
                      </div>
                    )}

                    {notApplicableControls}

                    {allowNotes && (
                      <div>
                        <label style={styles.fieldLabel}>Notes / observations</label>
                        <textarea
                          style={styles.textarea}
                          rows={3}
                          value={step.notes || ''}
                          onChange={event => handleStepNotesChange(step.id, event.target.value)}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {previewImage && (
            <div style={styles.previewOverlay} onClick={closePreviewImages}>
              <div style={styles.previewModal} onClick={event => event.stopPropagation()}>
                <div style={styles.previewHeader}>
                  <div style={styles.previewTitle}>
                    <strong>{previewImage.label || 'Reference image'}</strong>
                    <span style={styles.previewCounter}>
                      {previewImageCount > 0 ? `${previewImageIndex + 1} / ${previewImageCount}` : null}
                    </span>
                  </div>
                  <button
                    type="button"
                    style={styles.previewCloseButton}
                    onClick={closePreviewImages}
                  >
                    Close
                  </button>
                </div>
                <div style={styles.previewControls}>
                  {previewImageCount > 1 && (
                    <>
                      <button
                        type="button"
                        style={styles.previewControlButton}
                        onClick={showPreviousPreviewImage}
                        disabled={previewImageIndex <= 0}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        style={styles.previewControlButton}
                        onClick={showNextPreviewImage}
                        disabled={previewImageIndex >= previewImageCount - 1}
                      >
                        Next
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    style={styles.previewControlButton}
                    onClick={() => setPreviewZoom(value => Math.max(100, value - 25))}
                    aria-label="Zoom out"
                  >
                    -
                  </button>
                  <input
                    type="range"
                    min={100}
                    max={300}
                    step={25}
                    value={previewZoom}
                    onChange={event => setPreviewZoom(Number(event.target.value))}
                    style={styles.previewZoomSlider}
                    aria-label="Image zoom"
                  />
                  <button
                    type="button"
                    style={styles.previewControlButton}
                    onClick={() => setPreviewZoom(value => Math.min(300, value + 25))}
                    aria-label="Zoom in"
                  >
                    +
                  </button>
                  <button
                    type="button"
                    style={styles.previewControlButton}
                    onClick={() => setPreviewZoom(100)}
                  >
                    100%
                  </button>
                  <span style={styles.previewZoomValue}>{previewZoom}%</span>
                </div>
                {previewImageCount > 1 && (
                  <div style={styles.previewGalleryStrip}>
                    {previewImages.map((image, index) => (
                      <button
                        type="button"
                        key={image.id || `${index}`}
                        style={{
                          ...styles.previewGalleryThumbButton,
                          ...(index === previewImageIndex
                            ? styles.previewGalleryThumbButtonActive
                            : {})
                        }}
                        onClick={() => selectPreviewImage(index)}
                        aria-label={`Open reference image ${index + 1}`}
                      >
                        <AuthenticatedReferenceImage
                          src={image.fileUrl}
                          alt={image.label || 'reference image'}
                          style={styles.previewGalleryThumb}
                          placeholderStyle={styles.previewGalleryThumbPlaceholder}
                        />
                      </button>
                    ))}
                  </div>
                )}
                <div style={styles.previewImageStage}>
                  <div
                    style={{
                      ...styles.previewImageCanvas,
                      width: `${previewZoom}%`,
                      height: `${previewZoom}%`,
                      alignItems: previewZoom > 100 ? 'flex-start' : 'center',
                      justifyContent: previewZoom > 100 ? 'flex-start' : 'center'
                    }}
                  >
                    <AuthenticatedReferenceImage
                      src={previewImage.fileUrl}
                      alt="Reference image"
                      style={styles.previewImage}
                      placeholderStyle={styles.previewPlaceholder}
                      placeholderText="No preview available"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          <CaseAttachmentViewerModal
            attachment={attachmentViewer}
            onClose={() => setAttachmentViewer(null)}
            formatFileSize={formatAttachmentSize}
          />
          {feedbackModal && (
            <div style={styles.feedbackBackdrop} onClick={handleCloseFeedbackModal}>
              <div style={styles.feedbackModal} onClick={event => event.stopPropagation()}>
                <div style={styles.feedbackHeader}>
                  <div>
                    <div style={styles.feedbackTitle}>Flag documentation issue</div>
                    <div style={styles.feedbackSubtitle}>{feedbackModal.stepLabel}</div>
                  </div>
                  <button
                    type="button"
                    style={styles.feedbackClose}
                    onClick={handleCloseFeedbackModal}
                    aria-label="Close feedback modal"
                  >
                    &times;
                  </button>
                </div>
                <div style={styles.feedbackBody}>
                  <label style={styles.feedbackFieldLabel}>Issue type</label>
                  <select
                    style={styles.feedbackSelect}
                    value={feedbackForm.feedbackType}
                    onChange={event =>
                      setFeedbackForm(prev => ({ ...prev, feedbackType: event.target.value }))
                    }
                    disabled={feedbackSubmitting}
                  >
                    {FEEDBACK_TYPE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <label style={styles.feedbackFieldLabel}>Severity</label>
                  <select
                    style={styles.feedbackSelect}
                    value={feedbackForm.severity}
                    onChange={event =>
                      setFeedbackForm(prev => ({ ...prev, severity: event.target.value }))
                    }
                    disabled={feedbackSubmitting}
                  >
                    {FEEDBACK_SEVERITY_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <label style={styles.feedbackFieldLabel}>Issue title *</label>
                  <input
                    style={styles.feedbackInput}
                    type="text"
                    placeholder="Short summary"
                    value={feedbackForm.title}
                    onChange={event =>
                      setFeedbackForm(prev => ({ ...prev, title: event.target.value }))
                    }
                    disabled={feedbackSubmitting}
                  />

                  <label style={styles.feedbackFieldLabel}>Detailed description *</label>
                  <textarea
                    style={styles.feedbackTextarea}
                    rows={4}
                    placeholder="Describe what is wrong or missing..."
                    value={feedbackForm.description}
                    onChange={event =>
                      setFeedbackForm(prev => ({ ...prev, description: event.target.value }))
                    }
                    disabled={feedbackSubmitting}
                  />

                  <label style={styles.feedbackFieldLabel}>Impact (optional)</label>
                  <input
                    style={styles.feedbackInput}
                    type="text"
                    placeholder="e.g., Delays install by 30 minutes"
                    value={feedbackForm.impact}
                    onChange={event =>
                      setFeedbackForm(prev => ({ ...prev, impact: event.target.value }))
                    }
                    disabled={feedbackSubmitting}
                  />

                  {feedbackError && <div style={styles.feedbackError}>{feedbackError}</div>}
                  {feedbackSuccess && <div style={styles.feedbackSuccess}>{feedbackSuccess}</div>}
                </div>
                <div style={styles.feedbackActions}>
                  <button
                    type="button"
                    style={styles.feedbackCancel}
                    onClick={handleCloseFeedbackModal}
                    disabled={feedbackSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    style={{
                      ...styles.feedbackSubmit,
                      ...(feedbackSubmitting ? styles.feedbackSubmitDisabled : {})
                    }}
                    onClick={handleSubmitFeedback}
                    disabled={feedbackSubmitting}
                  >
                    {feedbackSubmitting ? 'Submitting...' : 'Submit feedback'}
                  </button>
                </div>
              </div>
            </div>
          )}
          <RepairServiceReportModal
            open={serviceReportModalOpen}
            document={serviceReportDraft}
            saving={serviceReportSaving}
            regenerating={serviceReportLoading}
            sending={serviceReportSending}
            error={serviceReportError}
            mode={serviceReportMode}
            subtitle={
              serviceReportMode === 'view'
                ? 'Locked saved service report snapshot'
                : 'Preview the template-filled report generated from this task summary.'
            }
            hint={
              serviceReportMode === 'view'
                ? savedServiceReportSent
                  ? 'This service report has already been sent to the customer.'
                  : 'This saved service report has not been sent yet. Regenerate it after summary or template changes, then save the refreshed report before sending.'
                : 'Review the generated service report before saving it to the case.'
            }
            canRegenerate={serviceReportMode === 'view' && canRebuildServiceReport}
            regenerateLabel="Regenerate service report"
            onRegenerate={handleCreateServiceReport}
            saveLabel={workspace?.serviceReport?.document ? 'Save refreshed report' : 'Create report'}
            canSend={
              serviceReportMode === 'view' &&
              !workspaceReadOnly &&
              taskStatus === 'COMPLETED' &&
              Boolean(workspace?.serviceReport?.id) &&
              String(workspace?.serviceReport?.status || '').toUpperCase() !== 'SENT'
            }
            sendLabel="Send service report"
            onSend={handleSendServiceReport}
            onClose={closeServiceReportModal}
            onSave={handleSaveServiceReport}
          />

          {completionGateIssues && (
            <div style={styles.feedbackBackdrop}>
              <div style={styles.completionGateModal}>
                <div style={styles.feedbackHeader}>
                  <div>
                    <div style={styles.feedbackTitle}>Completion blocked</div>
                    <div style={styles.feedbackSubtitle}>
                      Review unfinished checklist items before closing this phase.
                    </div>
                  </div>
                  <button
                    type="button"
                    style={styles.feedbackClose}
                    onClick={handleCloseCompletionGate}
                    aria-label="Close completion gate"
                  >
                    &times;
                  </button>
                </div>
                <div style={styles.completionGateBody}>
                  <div style={styles.completionGateNotice}>
                    This task does not validate hidden measurement ranges automatically. It is blocked
                    because required checklist items are not marked complete. Complete the items below,
                    mark a step not applicable with a reason, or force close with an explanation.
                  </div>
                  <div style={styles.completionIssueList}>
                    {completionGateIssues.map(issue => (
                      <div key={issue.stepId} style={styles.completionIssueCard}>
                        <div style={styles.completionIssueTitle}>{issue.stepLabel}</div>
                        <ul style={styles.completionIssueItems}>
                          {issue.itemLabels.map(item => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                  <label style={styles.feedbackFieldLabel}>Force-completion reason</label>
                  <textarea
                    style={styles.feedbackTextarea}
                    rows={4}
                    placeholder="Explain why this phase is ready to close despite the incomplete items..."
                    value={forceCompletionReason}
                    onChange={event => {
                      setForceCompletionReason(event.target.value);
                      setForceCompletionError(null);
                    }}
                  />
                  {forceCompletionError && (
                    <div style={styles.feedbackError}>{forceCompletionError}</div>
                  )}
                </div>
                <div style={styles.feedbackActions}>
                  <button
                    type="button"
                    style={styles.feedbackCancel}
                    onClick={handleCloseCompletionGate}
                    disabled={saving}
                  >
                    Go back
                  </button>
                  <button
                    type="button"
                    style={{
                      ...styles.feedbackSubmit,
                      ...(saving ? styles.feedbackSubmitDisabled : {})
                    }}
                    onClick={handleConfirmForcedCompletion}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Force close with reason'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      </fieldset>
    </div>
  );
};

const PRE_INSTALL_CONFIG: InstallPhaseConfig = {
  phase: 'PREP',
  api: installPrepApi,
  copy: {
    caseLabel: 'Install Preparation',
    totalTimeLabel: 'Total prep time (hours)',
    headerStatus: {
      IN_PROGRESS: 'In progress',
      ON_HOLD: 'On hold',
      COMPLETED: 'Ready for install'
    },
    statusButtonLabels: {
      IN_PROGRESS: 'In progress',
      ON_HOLD: 'On hold',
      COMPLETED: 'Ready'
    },
    summaryPlaceholder: 'Generate a summary to capture key pre-install findings for the installation team.',
    summaryGenerateLabel: 'Generate summary',
    summaryGeneratingLabel: 'Generating...',
    summaryEditLabel: 'Edit summary',
    summaryNoDataLabel: 'No summary generated yet.',
    runNotesLabel: 'General notes',
    markCompleteButtonLabel: 'Mark ready for install',
    loadingMessage: 'Loading pre-installation checklist...',
    loadError: 'Failed to load pre-install task',
    saveError: 'Failed to save pre-install task'
  }
};

const InstallPrepTask: React.FC = () => {
  return <InstallPhaseWorkspace config={PRE_INSTALL_CONFIG} />;
};

export default InstallPrepTask;
export { InstallPhaseWorkspace };
export type { InstallPhaseConfig };

const styles: Record<string, React.CSSProperties> = {
  page: {
    background: BRAND_SURFACE,
    minHeight: '100vh',
    padding: '2.5rem 3rem 4rem',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1.75rem',
    maxWidth: '1600px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box' as const
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap' as const
  },
  backButton: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.8rem',
    padding: '0.7rem 1rem',
    color: BRAND_TEXT,
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: '0.95rem',
    textDecoration: 'none',
    boxShadow: '0 10px 24px rgba(18, 63, 67, 0.08)'
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap' as const,
    justifyContent: 'flex-end'
  },
  readOnlyBanner: {
    background: BRAND_TEXT_SOFT,
    color: BRAND_TEXT,
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    borderRadius: '0.9rem',
    padding: '0.9rem 1.2rem',
    fontWeight: 600,
    marginBottom: '1rem'
  },
  readOnlyFieldset: {
    border: 'none',
    margin: 0,
    padding: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1.5rem'
  },
  savedAt: {
    fontSize: '0.85rem',
    color: BRAND_MUTED
  },
  referenceTag: {
    background: BRAND_SUCCESS_SOFT,
    color: BRAND_SUCCESS_TEXT,
    border: `1px solid ${BRAND_SUCCESS_BORDER}`,
    padding: '0.35rem 0.75rem',
    borderRadius: '999px',
    fontSize: '0.8rem',
    fontWeight: 600
  },
  referenceTagDraft: {
    background: BRAND_ACCENT_SOFT,
    color: BRAND_ACCENT_TEXT,
    border: `1px solid ${BRAND_ACCENT}`,
    padding: '0.35rem 0.75rem',
    borderRadius: '999px',
    fontSize: '0.8rem',
    fontWeight: 600
  },
  statusBadge: {
    padding: '0.45rem 1.1rem',
    borderRadius: '999px',
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.85rem',
    alignSelf: 'flex-start'
  },
  caseHeaderCard: {
    borderRadius: '1rem',
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    padding: '1.75rem',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1.25rem',
    boxShadow: '0 22px 48px rgba(18, 63, 67, 0.08)'
  },
  caseHeaderTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    flexWrap: 'wrap' as const
  },
  caseHeaderCopy: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.6rem',
    flex: '1 1 32rem'
  },
  caseHeaderStatus: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
    flex: '0 0 auto'
  },
  caseHeroActions: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap' as const,
    alignItems: 'flex-start',
    justifyContent: 'flex-end'
  },
  caseHeroTitle: {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: BRAND_TEXT
  },
  caseHeroProblem: {
    fontWeight: 600,
    color: BRAND_TEXT,
    maxWidth: '52rem',
    lineHeight: 1.4
  },
  caseHeaderGrid: {
    display: 'grid',
    gap: '1rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))'
  },
  caseHeaderField: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.35rem'
  },
  caseHeaderFieldLabel: {
    fontSize: '0.75rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: BRAND_MUTED,
    fontWeight: 700
  },
  caseHeaderFieldValue: {
    color: BRAND_TEXT,
    fontWeight: 600
  },
  caseHeaderLeft: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.4rem',
    flex: 1
  },
  caseHeaderRight: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
    alignItems: 'flex-end'
  },
  caseLabel: {
    fontSize: '0.85rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: BRAND_MUTED,
    fontWeight: 600
  },
  caseTitle: {
    fontWeight: 700,
    fontSize: '1.35rem',
    color: BRAND_TEXT
  },
  caseMeta: {
    color: BRAND_MUTED,
    fontSize: '0.95rem'
  },
  caseSubMeta: {
    color: BRAND_MUTED,
    fontSize: '0.9rem'
  },
  preInstallCard: {
    backgroundColor: BRAND_CARD,
    borderRadius: '1rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '1rem 1.1rem',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.85rem',
    boxShadow: '0 18px 40px rgba(18, 63, 67, 0.07)'
  },
  preInstallCardReady: {
    backgroundColor: BRAND_SUCCESS_SOFT,
    borderColor: BRAND_SUCCESS_BORDER
  },
  preInstallCompactRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: '1rem',
    alignItems: 'center'
  },
  preInstallCompactMain: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem'
  },
  preInstallTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.65rem',
    flexWrap: 'wrap' as const
  },
  preInstallCompactActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    justifyContent: 'flex-end',
    flexWrap: 'wrap' as const
  },
  preInstallHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem'
  },
  preInstallTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: BRAND_TEXT
  },
  preInstallSubtitle: {
    fontSize: '0.9rem',
    color: BRAND_MUTED,
    marginTop: '0.2rem'
  },
  preInstallBadge: {
    borderRadius: '999px',
    padding: '0.25rem 0.9rem',
    fontWeight: 600,
    fontSize: '0.8rem'
  },
  preInstallSummary: {
    fontSize: '0.9rem',
    color: BRAND_TEXT,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap' as const,
    border: `1px dashed ${BRAND_BORDER}`,
    borderRadius: '0.75rem',
    backgroundColor: '#fff',
    padding: '0.85rem'
  },
  preInstallDetails: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
    borderTop: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    paddingTop: '0.85rem'
  },
  preInstallListTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: BRAND_TEXT
  },
  preInstallList: {
    margin: 0,
    paddingLeft: '1.25rem',
    color: BRAND_TEXT,
    fontSize: '0.9rem',
    lineHeight: 1.4
  },
  preInstallNote: {
    backgroundColor: BRAND_SURFACE_SOFT,
    borderRadius: '0.85rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.9rem'
  },
  preInstallNoteLabel: {
    fontSize: '0.75rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    color: BRAND_MUTED,
    marginBottom: '0.35rem'
  },
  preInstallActions: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  preInstallActionButton: {
    border: `1px solid ${BRAND_BORDER}`,
    backgroundColor: BRAND_CARD,
    color: BRAND_TEXT,
    borderRadius: '0.8rem',
    padding: '0.65rem 1rem',
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: '2.7rem',
    boxShadow: '0 10px 24px rgba(18, 63, 67, 0.08)'
  },
  preInstallGhostButton: {
    border: `1px solid ${BRAND_BORDER}`,
    backgroundColor: '#fff',
    color: BRAND_TEXT,
    borderRadius: '0.75rem',
    padding: '0.55rem 0.85rem',
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: '2.45rem'
  },
  preInstallDetailsButton: {
    border: 'none',
    background: 'transparent',
    color: BRAND_TEXT,
    fontWeight: 700,
    cursor: 'pointer',
    padding: '0.45rem 0.2rem',
    textDecoration: 'underline',
    textUnderlineOffset: '2px'
  },
  statsCard: {
    borderRadius: '1rem',
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    padding: '1.75rem',
    boxShadow: '0 18px 40px rgba(18, 63, 67, 0.07)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1.25rem'
  },
  statusHeader: {
    display: 'flex',
    gap: '1.5rem',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap' as const
  },
  statusLeft: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.85rem',
    minWidth: 0,
    flex: 1
  },
  statusChipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  statusMetaRow: {
    display: 'flex',
    gap: '1.25rem',
    flexWrap: 'wrap' as const
  },
  statusMetaItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem'
  },
  statusMetaValue: {
    color: BRAND_TEXT,
    fontWeight: 600
  },
  statusMetaSubnote: {
    color: BRAND_MUTED,
    fontSize: '0.8rem',
    lineHeight: 1.4
  },
  statusRight: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
    minWidth: '15rem',
    alignItems: 'flex-end'
  },
  primaryActions: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.75rem',
    justifyContent: 'flex-end'
  },
  statusChip: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: '9999px',
    padding: '0.45rem 1.1rem',
    fontSize: '0.85rem',
    fontWeight: 700
  },
  statusActionComplete: {
    background: BRAND_TEXT,
    border: `1px solid ${BRAND_TEXT}`,
    color: '#ffffff',
    borderRadius: '0.8rem',
    padding: '0.7rem 1.5rem',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
    minHeight: '2.8rem',
    minWidth: '10.5rem',
    boxShadow: '0 16px 28px rgba(18, 63, 67, 0.18)'
  },
  statusActionHold: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_ACCENT}`,
    color: BRAND_ACCENT_TEXT,
    borderRadius: '0.8rem',
    padding: '0.7rem 1.5rem',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
    minHeight: '2.8rem',
    minWidth: '10.5rem',
    boxShadow: '0 10px 24px rgba(18, 63, 67, 0.08)'
  },
  statusActionReopen: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    color: BRAND_MUTED,
    borderRadius: '0.8rem',
    padding: '0.7rem 1.5rem',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
    minHeight: '2.8rem',
    minWidth: '10.5rem',
    boxShadow: '0 10px 24px rgba(18, 63, 67, 0.08)'
  },
  statusActionDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  statsRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '1.25rem',
    alignItems: 'flex-start'
  },
  statLabel: {
    fontSize: '0.8rem',
    color: BRAND_MUTED,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    marginBottom: '0.35rem'
  },
  timeInput: {
    borderRadius: '0.75rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.6rem 0.9rem',
    width: '180px',
    fontSize: '1rem',
    background: '#ffffff',
    color: BRAND_TEXT
  },
  statusOptions: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap' as const
  },
  statusButton: {
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.65rem 1rem',
    fontWeight: 700,
    background: BRAND_CARD,
    color: BRAND_TEXT,
    cursor: 'pointer',
    minHeight: '2.7rem',
    minWidth: '9rem',
    boxShadow: '0 10px 24px rgba(18, 63, 67, 0.08)'
  },
  statusButtonActive: {
    background: BRAND_TEXT,
    borderColor: BRAND_TEXT,
    color: '#ffffff',
    boxShadow: '0 16px 28px rgba(18, 63, 67, 0.18)'
  },
  errorBanner: {
    border: `1px solid ${BRAND_DANGER_BORDER}`,
    background: BRAND_DANGER_SOFT,
    color: BRAND_DANGER_TEXT,
    padding: '0.75rem',
    borderRadius: '0.8rem',
    marginBottom: '1rem'
  },
  loadingState: {
    padding: '2rem',
    textAlign: 'center' as const,
    color: BRAND_MUTED
  },
  stepCard: {
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '1rem',
    padding: '1.25rem',
    marginBottom: '1rem',
    backgroundColor: BRAND_CARD,
    boxShadow: '0 16px 34px rgba(18, 63, 67, 0.06)'
  },
  stepCardComplete: {
    backgroundColor: BRAND_SUCCESS_SOFT,
    borderColor: BRAND_SUCCESS_BORDER
  },
  stepCardPartial: {
    backgroundColor: BRAND_ACCENT_SOFT,
    borderColor: BRAND_ACCENT
  },
  stepWrapper: {
    marginBottom: '1rem'
  },
  siteCard: {
    borderRadius: '1rem',
    border: `1px solid ${BRAND_BORDER}`,
    background: BRAND_SURFACE_SOFT,
    padding: '1.25rem',
    boxShadow: '0 16px 34px rgba(18, 63, 67, 0.06)'
  },
  siteCardComplete: {
    background: BRAND_SUCCESS_SOFT,
    borderColor: BRAND_SUCCESS_BORDER
  },
  siteCardPartial: {
    background: BRAND_ACCENT_SOFT,
    borderColor: BRAND_ACCENT
  },
  siteHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.75rem',
    gap: '0.75rem',
    flexWrap: 'wrap' as const
  },
  siteHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap' as const,
    justifyContent: 'flex-end'
  },
  siteTitle: {
    fontSize: '1.05rem',
    fontWeight: 700,
    color: BRAND_TEXT
  },
  siteMetaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  siteMeta: {
    fontSize: '0.85rem',
    color: BRAND_MUTED,
    fontWeight: 600
  },
  collapseButton: {
    border: `1px solid ${BRAND_TEXT}`,
    background: BRAND_TEXT,
    color: '#fff',
    padding: '0.55rem 0.95rem',
    borderRadius: '0.75rem',
    cursor: 'pointer',
    fontWeight: 700,
    minHeight: '2.4rem',
    boxShadow: '0 16px 24px rgba(18, 63, 67, 0.18)'
  },
  flagButton: {
    border: `1px solid ${BRAND_BORDER}`,
    background: BRAND_CARD,
    color: BRAND_TEXT,
    padding: '0.55rem 0.95rem',
    borderRadius: '0.75rem',
    cursor: 'pointer',
    fontWeight: 700,
    minHeight: '2.4rem',
    boxShadow: '0 10px 24px rgba(18, 63, 67, 0.08)'
  },
  siteChecklist: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.45rem',
    marginBottom: '1rem'
  },
  siteChecklistItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.35rem',
    fontSize: '0.95rem',
    color: BRAND_TEXT
  },
  siteChecklistToggle: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.5rem'
  },
  siteChecklistText: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.2rem'
  },
  siteChecklistLabel: {
    fontWeight: 400
  },
  siteChecklistSpec: {
    fontSize: '0.85rem',
    color: BRAND_MUTED
  },
  inlineImageTextLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    color: BRAND_TEXT,
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    cursor: 'pointer',
    font: 'inherit',
    textAlign: 'left'
  },
  siteAttachments: {
    border: `1px dashed ${BRAND_BORDER}`,
    borderRadius: '0.85rem',
    padding: '0.85rem',
    background: '#fff'
  },
  siteAttachmentsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '0.5rem',
    flexWrap: 'wrap' as const
  },
  siteAttachmentHint: {
    fontSize: '0.85rem',
    color: BRAND_MUTED
  },
  addPhotoButton: {
    background: BRAND_TEXT,
    color: '#fff',
    border: `1px solid ${BRAND_TEXT}`,
    padding: '0.65rem 1rem',
    borderRadius: '0.8rem',
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: '2.7rem',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 16px 24px rgba(18, 63, 67, 0.18)'
  },
  stepHeader: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.75rem',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.75rem'
  },
  stepHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    flexWrap: 'wrap' as const,
    justifyContent: 'flex-end'
  },
  stepLabel: {
    fontWeight: 600,
    color: BRAND_TEXT
  },
  stepMetaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  stepMeta: {
    fontSize: '0.8rem',
    color: BRAND_MUTED
  },
  inlineToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    fontSize: '0.85rem',
    color: BRAND_MUTED
  },
  checklist: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.65rem',
    marginBottom: '0.75rem'
  },
  checklistItem: {
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.65rem',
    padding: '0.75rem',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.35rem',
    background: '#fff'
  },
  checklistLabel: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'flex-start',
    color: BRAND_TEXT,
    fontWeight: 500
  },
  checklistPrimaryText: {
    fontWeight: 600
  },
  checklistDetailText: {
    fontWeight: 400,
    color: BRAND_MUTED
  },
  checklistContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.35rem'
  },
  checklistSpec: {
    display: 'block',
    fontSize: '0.85rem',
    color: BRAND_MUTED,
    fontWeight: 400,
    marginTop: '0.2rem'
  },
  captureRow: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.35rem'
  },
  captureInputGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
    maxWidth: '16rem'
  },
  captureLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.45rem',
    flexWrap: 'wrap' as const
  },
  captureLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: BRAND_TEXT
  },
  captureDetailsToggle: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    color: BRAND_TEXT,
    cursor: 'pointer',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    fontSize: '0.75rem',
    fontWeight: 600
  },
  captureInput: {
    borderRadius: '0.6rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.45rem 0.6rem',
    fontSize: '0.9rem',
    color: BRAND_TEXT,
    background: BRAND_CARD
  },
  captureInputValid: {
    borderColor: BRAND_SUCCESS_BORDER,
    background: BRAND_SUCCESS_SOFT
  },
  captureInputWarning: {
    borderColor: BRAND_ACCENT,
    background: BRAND_ACCENT_SOFT
  },
  captureInputInvalid: {
    borderColor: BRAND_DANGER_BORDER,
    background: BRAND_DANGER_SOFT
  },
  captureHint: {
    fontSize: '0.75rem',
    color: BRAND_MUTED
  },
  captureFeedback: {
    fontSize: '0.75rem',
    fontWeight: 600
  },
  checklistNotes: {
    width: '100%',
    borderRadius: '0.5rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.5rem',
    fontSize: '0.9rem',
    background: '#fff',
    color: BRAND_TEXT
  },
  attachments: {
    border: `1px dashed ${BRAND_BORDER}`,
    borderRadius: '0.65rem',
    padding: '0.75rem',
    marginBottom: '0.75rem',
    background: '#fff'
  },
  attachmentsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
    fontWeight: 600
  },
  uploadLabel: {
    color: BRAND_TEXT,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '2.55rem',
    padding: '0.55rem 0.95rem',
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.75rem',
    background: '#ffffff',
    boxShadow: '0 10px 24px rgba(18, 63, 67, 0.08)'
  },
  uploadLabelDisabled: {
    opacity: 0.65,
    cursor: 'wait'
  },
  attachmentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '0.65rem',
    marginTop: '0.65rem'
  },
  attachmentCard: {
    display: 'grid',
    gridTemplateColumns: '4.5rem minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: '0.65rem',
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.65rem',
    padding: '0.45rem',
    background: BRAND_SURFACE_SOFT,
    minWidth: 0
  },
  attachmentThumbnailButton: {
    width: '4.5rem',
    height: '3.4rem',
    padding: 0,
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    borderRadius: '0.5rem',
    background: '#fff',
    overflow: 'hidden',
    cursor: 'pointer'
  },
  attachmentThumb: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    display: 'block'
  },
  attachmentThumbPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.25rem',
    background: BRAND_TEXT_SOFT,
    color: BRAND_TEXT,
    fontSize: '0.72rem',
    fontWeight: 700,
    textAlign: 'center' as const
  },
  attachmentMeta: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.18rem'
  },
  attachmentNameButton: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    color: BRAND_TEXT,
    fontWeight: 700,
    cursor: 'pointer',
    textAlign: 'left' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const
  },
  attachmentSubtext: {
    color: BRAND_MUTED,
    fontSize: '0.78rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const
  },
  removeAttachment: {
    background: 'none',
    border: 'none',
    color: BRAND_DANGER_TEXT,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    fontWeight: 600
  },
  removeAttachmentDisabled: {
    color: BRAND_MUTED,
    cursor: 'wait'
  },
  attachmentPlaceholder: {
    fontSize: '0.85rem',
    color: BRAND_MUTED
  },
  previewOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    zIndex: 1200
  },
  previewModal: {
    backgroundColor: BRAND_CARD,
    borderRadius: '8px',
    padding: '0.75rem',
    width: 'min(94vw, 1400px)',
    height: '90vh',
    maxHeight: '940px',
    border: `1px solid ${BRAND_BORDER}`,
    boxShadow: '0 25px 65px rgba(15, 23, 42, 0.35)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.65rem',
    overflow: 'hidden'
  },
  previewHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '1rem',
    padding: 0
  },
  previewTitle: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.3rem',
    minWidth: 0
  },
  previewCounter: {
    color: BRAND_MUTED,
    fontSize: '0.85rem'
  },
  previewCloseButton: {
    border: `1px solid ${BRAND_BORDER}`,
    backgroundColor: BRAND_CARD,
    color: BRAND_TEXT,
    borderRadius: '0.5rem',
    padding: '0.4rem 0.75rem',
    fontWeight: 600,
    cursor: 'pointer'
  },
  previewControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.45rem',
    flexWrap: 'wrap' as const,
    borderTop: `1px solid ${BRAND_BORDER}`,
    paddingTop: '0.55rem'
  },
  previewControlButton: {
    border: `1px solid ${BRAND_BORDER}`,
    backgroundColor: BRAND_CARD,
    borderRadius: '6px',
    padding: '0.32rem 0.58rem',
    fontWeight: 600,
    color: BRAND_TEXT,
    cursor: 'pointer',
    minWidth: '2.35rem'
  },
  previewZoomSlider: {
    width: 'min(34vw, 420px)',
    maxWidth: '100%'
  },
  previewZoomValue: {
    color: BRAND_MUTED,
    fontSize: '0.9rem',
    minWidth: '3rem'
  },
  previewGalleryStrip: {
    display: 'flex',
    gap: '0.45rem',
    overflowX: 'auto',
    paddingBottom: '0.15rem'
  },
  previewGalleryThumbButton: {
    width: '86px',
    height: '60px',
    flex: '0 0 auto',
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '6px',
    padding: 0,
    overflow: 'hidden',
    backgroundColor: BRAND_CARD,
    cursor: 'pointer'
  },
  previewGalleryThumbButtonActive: {
    borderColor: BRAND_TEXT,
    boxShadow: '0 0 0 2px rgba(18, 63, 67, 0.18)'
  },
  previewGalleryThumb: {
    width: '100%',
    height: '100%',
    objectFit: 'contain' as const,
    display: 'block'
  },
  previewGalleryThumbPlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: BRAND_SURFACE_SOFT,
    color: BRAND_MUTED,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center' as const,
    fontSize: '0.72rem'
  },
  previewImageStage: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    backgroundColor: BRAND_SURFACE_SOFT,
    borderRadius: '6px',
    padding: '0.75rem'
  },
  previewImageCanvas: {
    minWidth: '100%',
    minHeight: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  previewImage: {
    width: '100%',
    height: '100%',
    maxWidth: 'none',
    maxHeight: 'none',
    objectFit: 'contain' as const,
    display: 'block',
    backgroundColor: BRAND_SURFACE_SOFT
  },
  previewPlaceholder: {
    width: '100%',
    height: '320px',
    color: BRAND_MUTED,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center' as const,
    padding: '1rem',
    backgroundColor: BRAND_SURFACE_SOFT,
    borderRadius: '0.65rem'
  },
  notApplicableBoxSite: {
    border: `1px dashed ${BRAND_BORDER}`,
    borderRadius: '0.75rem',
    padding: '0.85rem',
    marginTop: '0.85rem',
    background: '#fff'
  },
  notApplicableBox: {
    border: `1px dashed ${BRAND_BORDER}`,
    borderRadius: '0.75rem',
    padding: '0.85rem',
    marginTop: '0.85rem',
    background: '#fff'
  },
  notApplicableHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap' as const,
    marginBottom: '0.5rem'
  },
  notApplicableTitle: {
    fontWeight: 600,
    color: BRAND_TEXT
  },
  notApplicableSelect: {
    width: '100%',
    borderRadius: '0.65rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.5rem',
    fontSize: '0.9rem',
    marginBottom: '0.5rem',
    background: '#fff',
    color: BRAND_TEXT
  },
  notApplicableNotes: {
    width: '100%',
    borderRadius: '0.65rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.5rem',
    fontSize: '0.9rem',
    background: '#fff',
    color: BRAND_TEXT
  },
  notApplicableHint: {
    fontSize: '0.8rem',
    color: BRAND_MUTED,
    marginTop: '0.25rem'
  },
  notApplicableBadge: {
    backgroundColor: BRAND_ACCENT_SOFT,
    color: BRAND_ACCENT_TEXT,
    borderRadius: '999px',
    padding: '0.2rem 0.65rem',
    fontSize: '0.75rem',
    fontWeight: 600
  },
  fieldLabel: {
    fontWeight: 600,
    display: 'block',
    marginBottom: '0.35rem'
  },
  textarea: {
    width: '100%',
    borderRadius: '0.75rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.75rem',
    fontSize: '0.95rem',
    resize: 'vertical' as const,
    background: '#ffffff',
    color: BRAND_TEXT
  },
  runNotes: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.35rem',
    marginTop: '0.5rem'
  },
  reportCard: {
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '1rem',
    padding: '1.5rem 1.75rem',
    backgroundColor: BRAND_CARD,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
    boxShadow: '0 18px 40px rgba(18, 63, 67, 0.07)'
  },
  reportHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    flexWrap: 'wrap' as const,
    alignItems: 'center'
  },
  reportTitle: {
    fontWeight: 700,
    fontSize: '1rem',
    color: BRAND_TEXT
  },
  reportMeta: {
    fontSize: '0.85rem',
    color: BRAND_MUTED,
    marginTop: '0.25rem'
  },
  reportActions: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap' as const
  },
  reportPrimaryButton: {
    border: `1px solid ${BRAND_TEXT}`,
    backgroundColor: BRAND_TEXT,
    color: '#fff',
    borderRadius: '0.8rem',
    padding: '0.65rem 1rem',
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: '2.7rem',
    boxShadow: '0 16px 24px rgba(18, 63, 67, 0.18)'
  },
  reportSecondaryButton: {
    border: `1px solid ${BRAND_BORDER}`,
    backgroundColor: '#fff',
    color: BRAND_TEXT,
    borderRadius: '0.8rem',
    padding: '0.6rem 0.95rem',
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: '2.6rem',
    boxShadow: '0 10px 24px rgba(18, 63, 67, 0.08)'
  },
  reportBody: {
    border: `1px dashed ${BRAND_BORDER}`,
    borderRadius: '0.75rem',
    padding: '0.85rem',
    minHeight: '120px',
    whiteSpace: 'pre-wrap' as const,
    lineHeight: 1.5,
    color: BRAND_TEXT,
    background: '#fff'
  },
  reportTextarea: {
    width: '100%',
    borderRadius: '0.75rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.75rem',
    fontFamily: 'inherit',
    fontSize: '0.95rem',
    lineHeight: 1.5,
    background: '#fff',
    color: BRAND_TEXT
  },
  reportEditActions: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap' as const
  },
  reportActionButton: {
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: BRAND_TEXT,
    border: `1px solid ${BRAND_TEXT}`,
    color: '#ffffff',
    borderRadius: '0.8rem',
    padding: '0.75rem 1.5rem',
    fontWeight: 700,
    fontSize: '0.95rem',
    cursor: 'pointer',
    alignSelf: 'stretch',
    minWidth: '16rem',
    minHeight: '2.8rem',
    boxShadow: '0 16px 24px rgba(18, 63, 67, 0.18)'
  },
  reportActionButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  reportInfo: {
    color: BRAND_MUTED,
    fontSize: '0.8rem',
    textAlign: 'right' as const
  },
  reportInfoError: {
    color: BRAND_DANGER_TEXT,
    fontSize: '0.85rem',
    textAlign: 'right' as const
  },
  expertSection: {
    background: 'linear-gradient(180deg, #F7F3EA 0%, #F1F6F3 100%)',
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    borderRadius: '1rem',
    padding: '1.35rem 1.5rem',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
    boxShadow: '0 18px 34px -30px rgba(18, 63, 67, 0.26)'
  },
  expertHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    flexWrap: 'wrap' as const
  },
  expertHeaderCopy: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.35rem',
    flex: '1 1 20rem'
  },
  expertTitle: {
    fontSize: '1.05rem',
    fontWeight: 700,
    color: BRAND_TEXT
  },
  expertSub: {
    color: BRAND_MUTED,
    fontSize: '0.9rem',
    lineHeight: 1.5
  },
  expertGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '0.85rem'
  },
  expertField: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.4rem'
  },
  expertLabel: {
    fontSize: '0.78rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    color: BRAND_MUTED
  },
  expertSelect: {
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.7rem 0.85rem',
    width: '100%',
    fontSize: '0.95rem',
    background: '#FCFBF7',
    color: BRAND_TEXT
  },
  expertInput: {
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.8rem 0.95rem',
    width: '100%',
    fontSize: '0.95rem',
    background: '#ffffff',
    color: BRAND_TEXT
  },
  expertHint: {
    color: BRAND_MUTED,
    fontSize: '0.85rem',
    lineHeight: 1.5
  },
  expertActions: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap' as const
  },
  expertMessages: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.65rem'
  },
  expertMessage: {
    borderRadius: '0.9rem',
    padding: '0.8rem 0.95rem',
    lineHeight: 1.55,
    fontSize: '0.93rem',
    whiteSpace: 'pre-wrap' as const
  },
  expertAssistant: {
    background: BRAND_SURFACE_SOFT,
    border: `1px solid ${BRAND_BORDER}`,
    color: BRAND_TEXT
  },
  expertUser: {
    background: BRAND_TEXT_SOFT,
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    color: BRAND_TEXT
  },
  expertError: {
    borderRadius: '0.8rem',
    padding: '0.75rem 0.9rem',
    background: BRAND_DANGER_SOFT,
    border: `1px solid ${BRAND_DANGER_BORDER}`,
    color: BRAND_DANGER_TEXT,
    fontWeight: 600
  },
  reportError: {
    color: BRAND_DANGER_TEXT,
    fontSize: '0.9rem',
    fontWeight: 600
  },
  statsActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap' as const,
    gap: '0.75rem'
  },
  unsaved: {
    fontSize: '0.85rem',
    color: BRAND_ACCENT_TEXT,
    fontWeight: 600
  },
  footerActions: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap' as const
  },
  button: {
    border: '1px solid transparent',
    borderRadius: '0.8rem',
    padding: '0.7rem 1.2rem',
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: '2.7rem',
    boxShadow: '0 10px 24px rgba(18, 63, 67, 0.08)'
  },
  primaryButton: {
    backgroundColor: BRAND_TEXT,
    borderColor: BRAND_TEXT,
    color: '#fff',
    boxShadow: '0 16px 24px rgba(18, 63, 67, 0.18)'
  },
  ghostButton: {
    backgroundColor: '#fff',
    color: BRAND_TEXT,
    borderColor: BRAND_BORDER,
    boxShadow: '0 10px 24px rgba(18, 63, 67, 0.08)'
  },
  secondaryButton: {
    backgroundColor: '#ffffff',
    borderColor: BRAND_BORDER,
    color: BRAND_TEXT,
    boxShadow: '0 10px 24px rgba(18, 63, 67, 0.08)'
  },
  feedbackBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(18, 63, 67, 0.32)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem',
    zIndex: 1000
  },
  feedbackModal: {
    backgroundColor: BRAND_CARD,
    borderRadius: '1rem',
    width: '100%',
    maxWidth: '520px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 30px 70px rgba(18, 63, 67, 0.16)',
    display: 'flex',
    flexDirection: 'column' as const
  },
  completionGateModal: {
    backgroundColor: BRAND_CARD,
    borderRadius: '1rem',
    width: 'min(760px, 100%)',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: '0 30px 70px rgba(18, 63, 67, 0.16)',
    display: 'flex',
    flexDirection: 'column' as const
  },
  feedbackHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.25rem 1.25rem 0.5rem 1.25rem',
    borderBottom: `1px solid ${BRAND_BORDER}`
  },
  feedbackTitle: {
    fontSize: '1.05rem',
    fontWeight: 700,
    color: BRAND_TEXT
  },
  feedbackSubtitle: {
    fontSize: '0.85rem',
    color: BRAND_MUTED,
    marginTop: '0.2rem'
  },
  feedbackClose: {
    border: 'none',
    background: 'transparent',
    color: BRAND_TEXT,
    fontSize: '1.5rem',
    cursor: 'pointer'
  },
  feedbackBody: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
    padding: '1rem 1.25rem'
  },
  completionGateBody: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.9rem',
    padding: '1rem 1.25rem',
    overflowY: 'auto'
  },
  completionGateNotice: {
    border: `1px solid ${BRAND_ACCENT}`,
    background: BRAND_ACCENT_SOFT,
    color: BRAND_TEXT,
    borderRadius: '0.85rem',
    padding: '0.85rem',
    lineHeight: 1.5
  },
  completionIssueList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem'
  },
  completionIssueCard: {
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.85rem',
    padding: '0.85rem',
    background: BRAND_SURFACE_SOFT
  },
  completionIssueTitle: {
    fontWeight: 800,
    color: BRAND_TEXT,
    marginBottom: '0.45rem'
  },
  completionIssueItems: {
    margin: 0,
    paddingLeft: '1.25rem',
    color: BRAND_TEXT,
    lineHeight: 1.5
  },
  feedbackFieldLabel: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: BRAND_TEXT
  },
  feedbackSelect: {
    borderRadius: '0.65rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.45rem',
    fontSize: '0.9rem',
    backgroundColor: '#fff',
    color: BRAND_TEXT
  },
  feedbackInput: {
    borderRadius: '0.65rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.5rem',
    fontSize: '0.9rem',
    background: '#fff',
    color: BRAND_TEXT
  },
  feedbackTextarea: {
    borderRadius: '0.65rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.5rem',
    fontSize: '0.9rem',
    resize: 'vertical' as const,
    background: '#fff',
    color: BRAND_TEXT
  },
  feedbackError: {
    color: BRAND_DANGER_TEXT,
    fontSize: '0.85rem',
    fontWeight: 600
  },
  feedbackSuccess: {
    color: BRAND_SUCCESS_TEXT,
    fontSize: '0.85rem',
    fontWeight: 600
  },
  feedbackActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    padding: '0.75rem 1.25rem 1.25rem 1.25rem'
  },
  feedbackCancel: {
    border: `1px solid ${BRAND_BORDER}`,
    background: '#fff',
    color: BRAND_TEXT,
    borderRadius: '0.8rem',
    padding: '0.65rem 1rem',
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: '2.7rem',
    boxShadow: '0 10px 24px rgba(18, 63, 67, 0.08)'
  },
  feedbackSubmit: {
    border: `1px solid ${BRAND_TEXT}`,
    background: BRAND_TEXT,
    color: '#fff',
    borderRadius: '0.8rem',
    padding: '0.65rem 1.15rem',
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: '2.7rem',
    boxShadow: '0 16px 24px rgba(18, 63, 67, 0.18)'
  },
  feedbackSubmitDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed'
  }
};

